import { Component, ElementRef, Input, Output, EventEmitter, OnInit, Renderer2, ViewChild ,ChangeDetectorRef} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, AbstractControl, FormsModule } from '@angular/forms';
import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { SettingsService } from 'src/app/shared/services/settings';
import { RichTextService } from 'src/app/shared/services/rich-text/rich-text.service';
import { ConversationService } from 'src/app/shared/services/conversation/conversation.service';
import { ltmsConversationDefaultProps } from '../../constants/default';
import * as _ from 'lodash'
import { QuillModule, QuillEditorComponent } from 'ngx-quill';
import { resolve } from 'path';
import { SchemaProvider } from 'src/app/shared/constants/shema-provider';
import { QueryBuilderService } from '../../services/query-builder.service';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { LtmsLoaderComponent } from '../../../../../../shared/components/ltms-loader/ltms-loader.component';
import { InteractionParentChildLinkageComponent } from './sub-components/interaction-parent-child-linkage/interaction-parent-child-linkage.component';
import { InteractionSetupOptionsComponent } from './sub-components/interaction-setup-options/interaction-setup-options.component';
import { InteractionConditionalLabelsComponent } from './sub-components/interaction-conditional-labels/interaction-conditional-labels.component';
import { applyCspNonce, installCspNonceForDynamicElements } from 'src/app/shared/utils/csp-nonce.util';

interface QuestionItem {
  id: number;
  type: string;
  typeIcon: string;
  text: string;
  isPrimary: boolean;
  isPrimaryComment: boolean;
  lastUpdated?: Date;
  expanded?: boolean;
  field_id?: string; // Add field_id property
}

interface PillValue {
  message: string;
  color: string;
  dashboardColor: string;
  activeColor: string;
}

interface ColorPicker {
  type: 'default' | 'dashboard' | 'active';
  index: number;
}

interface SurveyPage {
  id: number;
  name: string;
  type: 'default' | 'thank-you' | 'expired' | 'submitted';
  questions?: number[]; // IDs of questions on this page

  // Advanced options
  includeInProgress?: boolean;
  showProgressBar?: boolean;
  saveOnNext?: boolean;
  enableAutoScroll?: boolean;
  showNextButton?: boolean;
  nextButtonText?: string;
  backButtonText?: string;
  isLinkExpiredPage?: boolean;
  isResponseSubmittedPage?: boolean;
  isThankYouPage?: boolean;
  enablePageShuffling?: boolean;
}

interface QuestionComponent {
  _id: string;
  name: string;
  display_name?: string;
  category?: string;
  type?: string;
  display_on?: string;
  component_category?: string;
  scale_rater_type?: number;
  main_rater_type?: number;
  icon?: string;
  schema?: any;
  use_emoticon_rating?: boolean;
  rating_bar_reactor_allowed?: boolean;
  can_be_set_as_primary_comment?: boolean;
  is_allow_multi_select_display?: boolean;
}

interface Question {
  field_id: string;
  component_id?: string;
  component_type?: string;
  component_name?: string;
  component_category?: string;
  text?: string;
  question?: string; // Short text for dashboard
  label?: string;
  is_primary_rating?: boolean;
  is_primary_comment?: boolean;
  is_mandatory?: boolean;
  is_displayed_on_dashboard?: boolean;
  is_displayed_on_summary?: boolean;
  is_displayed_emoticon_on_dashboard?: boolean;
  is_calculate_mean_enabled?: boolean; // Add property for mean calculation
  type?: string;
  conversation_type?: string;
  hide_label?: boolean;
  do_not_repeat?: boolean;
  possible_values?: any[];
  sub_questions?: SubQuestion[];
  params?: any;
  style?: {
    text?: any;
    interaction?: any;
    [key: string]: any;
  };
  scale_rater_type?: number;
  main_rater_type?: number;
  rating_bar_reactor_allowed?: boolean;
  reactor_type?: string;
  rich_text_enabled?: boolean;
  rich_text?: string;
  allowMultiSelect?: boolean;
  enableParentChildLinkage?: boolean;
  enableConditionalLabels?: boolean;
  setupOptions?: boolean;
  enable_shuffle?: boolean;
  [key: string]: any; // Allow for additional properties
}

interface QuestionGroup {
  text: string;
  field_id: string;
  options: Question[];
}


interface SubQuestion {
  field_id: string;
  text: string;
  is_hidden?: boolean;
  enable_shuffle?: boolean;
  option_type?: string;
}

interface Condition {
  question: string;
  operator: string;
  value: string;
}

// Track if the script is loaded
let jsonEditorLoaded = false;
let jsonEditorLoadPromise: Promise<boolean> | null = null;
declare const JSONEditor: any;
declare const $: any;

// Add these interfaces
interface TransformCondition {
  interaction: string;
  comparisonOptr: string;
  value: string;
  target: string;
  configuration: any[];
  logicalOptr: string;
  isOpen: boolean;
}

interface LogicDetail {
  logicString: string | null;
  logicKey: string;
  editMode: boolean;
  transformString: TransformCondition[];
  transformOrignal: TransformCondition[];
}

interface ComponentConditions {
  logicDetail: {
    [key: string]: LogicDetail;
  };
  logicAction: string;
}

interface Operator {
  op: string;
  text: string;
}

interface Operators {
  logical: { [key: string]: Operator };
  comparison: { [key: string]: Operator };
}

@Component({
  selector: 'app-create-new-question',
  templateUrl: './create-new-question.component.html',
  styleUrls: ['./create-new-question.component.scss'],
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: '0', opacity: 0 }))
      ])
    ])
  ],
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    NgFor,
    QuillEditorComponent,
    NgClass,
    LtmsLoaderComponent,
    InteractionParentChildLinkageComponent,
    InteractionSetupOptionsComponent,
    InteractionConditionalLabelsComponent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle
  ]
})
export class CreateNewQuestionComponent implements OnInit {
  @ViewChild('componentSchema') componentSchemaRef!: ElementRef;
  @Output() closeEvent: any = new EventEmitter<any>();
  @Input() isEditMode: boolean = false;
  @Input() configuration: any = null;
  @Input() brandDetails: any = null;
  @Input() isDynamicPropertiesEnabled: boolean = false;
  @Input() dynamicPropertiesGroups: any = [{
    label: "Access Group",
    value: "access_group"
  }, {
    label: "Question Group",
    value: "question_group"
  }]
  questionForm!: FormGroup;
  rich_text_enabled: boolean = false;
  defaultSchemaEditorValues: any = {}
  defaultInteraction: any = {}
  isUserHasWriteAccess: boolean = false;
  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private settingService: SettingsService,
    private renderer: Renderer2,
    private richTextService: RichTextService,
    private _conversation: ConversationService,
    private queryBuilder: QueryBuilderService,
    private cdr: ChangeDetectorRef
  ) { }

  closeQuestionModal() {
    this.closeEvent.emit()
  }

  is_valid_page = false;
  account_id = '';
  brand_id = '';

  // Tab navigation
  activeTab = 'create'; // Set to 'all' for testing

  // Tooltip properties
  activeTooltip: QuestionItem | null = null;
  tooltipX = 0;
  tooltipY = 0;

  // Search functionality
  searchQuery = '';
  filteredQuestions: QuestionItem[] = [];

  // Question list for All Questions tab
  questionsList: QuestionItem[] = [];

  // Draft questions list
  draftQuestions: QuestionItem[] = [];

  // Question editor properties
  questionTitle = 'Based on your recent experience with our service/product, how likely are you to recommend us to a friend or colleague? Please rate on a scale from 0 to 10, where 0 is not at all likely and 10 is extremely likely.';
  questionType = '';
  questionDescription = '';
  category = 'NPS';
  raterType = '-5 to +5 Scale Horse Shoe Emoticon Wheel';

  // Preview properties
  currentDevice = 'desktop';
  previewLink = 'https://survey.litmusworld.com/samp12?preview=true&id=123456';
  showCopySuccess = false;

  // Property panel properties
  isPropertiesPanelOpen = true;
  activePropertyTab = 'basic';
  displayInConversation = true;
  isMandatory = false;
  isPrimaryQuestion = false;
  displayOnDashboard = true;
  selectedReactor = '';

  // Advanced properties
  defaultMessage = 'Please Rate';
  numberOfPills = 10;
  pillValues: PillValue[] = [];

  // Color picker properties
  activeColorPicker: ColorPicker | null = null;
  colorPickerX = 0;
  colorPickerY = 0;
  activeColorPickerValue = '#777777';
  predefinedColors = [
    '#FF5252', '#FF4081', '#E040FB', '#7C4DFF', '#536DFE', '#448AFF', '#40C4FF', '#18FFFF',
    '#64FFDA', '#69F0AE', '#B2FF59', '#EEFF41', '#FFFF00', '#FFD740', '#FFAB40', '#FF6E40',
    '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4',
    '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
    '#795548', '#9E9E9E', '#607D8B', '#000000', '#FFFFFF'
  ];

  // Pages tab properties
  surveyPages: SurveyPage[] = [];
  filteredPages: SurveyPage[] = [];
  pageSearchQuery = '';
  selectedPageIndex: number | null = null;
  selectedPage: SurveyPage | null = null;

  // Drag and drop properties
  draggedPageIndex: number | null = null;
  dragTargetIndex: number | null = null;

  // Additional options
  shortTextEnabled = false;
  shortText = '';
  dashboardVisibilityEnabled = true;
  visibilityOption = 'inbox';

  // Page Edit Modal properties
  showPageEditModal = false;
  activePageEditTab = 'basic';
  currentPageQuestions: QuestionItem[] = [];
  availableQuestions: QuestionItem[] = [];

  // New properties for simplified question selection UI
  questionSearchQuery = '';
  allQuestions: QuestionItem[] = [];
  filteredAllQuestions: QuestionItem[] = [];
  // New property to track if editor is ready to use
  isEditorReady = false;
  pendingSchemaData: any = null;
  maxRetries = 5;
  retryCount = 0;
  componentPropertyEditor: any = null
  showCalculateMean: boolean = false;
  @Input() question: Question | null = null;
  interaction: any = {}
  component_rater_type_mapping: any = {}
  isEditInteractionType: boolean = false;
  componentsHash: any = {}
  conversationFields: any = [];
  multiSelectModel = {};
  multiSelectData = [];
  isQuestionTextAvailable: boolean = true;




  interactionCount: number = 0
  questionFields: any = null;
  isIntractionLoaded: boolean = true;
  displayLabel: boolean = true;
  devices: any = []

  // Add at the top of the class
  isDirty: boolean = false;

  ngOnInit(): void {
    if (this.isEditMode) {
      this.isIntractionLoaded = false;
    }
    this.isUserHasWriteAccess = this._conversation.isUserHasWriteAccess('MANAGE_CONVERSATION');
    this.isEditInteractionType = false;
    this.loadComponentData().then(() => {
      this.devices = this._conversation.devices
      this.configuration = Helpers.deepClone(this._conversation.configuration);
      this.conversationFieldsHash = Helpers.deepClone(this._conversation.fieldsHash);
      this.conversationFields = Helpers.deepClone(this._conversation.configuration.fields);
      this.interactionCount = 0
      if (_.hasIn(this._conversation, 'configuration.fields')) {
        this.interactionCount = this._conversation.configuration.fields.length
        this.initializeQuestionsForCondition()
      }
      if (this.interactionCount < 1) {
        this.activeConditionTab = 'custom'
        this.componentConditions.logicAction = '3'
      }
      if (this.isEditMode && !_.isEmpty(this.question)) {
        this._conversation.selectedId = this.question?.field_id;
        this.selectedComponentId = this.question?.component_id;
        this.selectedComponent = this.componentsHash[this.selectedComponentId]
        const { category, label } = this._conversation.getComponentCategory(this.selectedComponent);
        this.selectedQuestionType = category || 'other';
        // this.selectedQuestionType = (this.selectedQuestionType == 'rating' && (this.selectedComponent.main_rater_type == 3 || this.selectedComponent.main_rater_type == '3')) ? 'nps' : this.selectedQuestionType;
        this.questionComponents = this.questionTypesMap[this.selectedQuestionType].components
        this.getComponentProperties(this.question?.component_id, 'edit')
        // this.getInteractionDetail(this.question?.component_id)

      } else {
        this.isEditInteractionType = true;
        this._conversation.selectedId = null;
        this.initInteraction()
      }
      // console.log("this.question", this.question)
      // Initialize filtered questions with all questions
      this.filteredQuestions = [...this.questionsList];

      // Initialize pill values
      this.initializePillValues();

      // Initialize filtered pages
      this.filteredPages = [...this.surveyPages];
      this.componentConditions = Helpers.deepClone(this.componentConditionsSchema);
      this.initializeConditions()
      if (this.isEditMode) {
        this.isIntractionLoaded = true;
      }
      if (this.isViewIntialized) {
        this.loadJsonEditor()
      }
    })

  }

  // getComponentIMG(device: string, component: any) {
  //   let deviceType: any = device
  //   let component_name = component.name;
  //   if (this.currentDevice == 'tablet') {
  //     deviceType = 'desktop'
  //   }
  //   if (component_name) {
  //     return `https://static1.litmusworld.com/component_screenshot/${deviceType}/${component_name}.png`
  //   } else {
  //     return `https://static1.litmusworld.com/component_screenshot/${deviceType}/no-preview.jpg`
  //   }
  // }


  isViewIntialized: boolean = false;
  ngAfterViewInit(): void {
    this.isViewIntialized = true
    if (this.isIntractionLoaded) {
      this.loadJsonEditor()
    }
  }

  loadJsonEditor() {
    // Load JSONEditor script dynamically
    this.loadJSONEditorScript().then(() => {
      this.isEditorReady = true;

      // If there's pending schema data, process it now
      if (this.pendingSchemaData) {
        this.drawComponentSchema(this.pendingSchemaData);
        this.pendingSchemaData = null;
      }

      // If we're in edit mode, try to load the schema for the existing component
      if (this.isEditMode && this.question && this.question?.component_id) {
        this.selectedComponentId = this.question?.component_id;
        this.selectedComponent = this.componentsHash[this.selectedComponentId]
        const { category, label } = this._conversation.getComponentCategory(this.selectedComponent);
        this.selectedQuestionType = category || 'other';
        // this.selectedQuestionType = (this.selectedQuestionType == 'rating' && (this.selectedComponent.main_rater_type == 3 || this.selectedComponent.main_rater_type == '3')) ? 'nps' : this.selectedQuestionType;
        this.questionComponents = this.questionTypesMap[this.selectedQuestionType].components

        this.getComponentSchema(this.question.component_id);
      }
    }).catch(error => {
      console.error('Failed to load JSONEditor:', error);

      if (this.componentSchemaRef?.nativeElement) {
        this.componentSchemaRef.nativeElement.innerHTML = `
          <div class="alert alert-danger">
            Failed to load the component editor. Please refresh the page and try again.
          </div>
        `;
      }
    });
  }

  initInteraction() {
    this.resetComponentEditor();
    // creating initial object for question
    var labelId = this.generateId('label') + Helpers.getRandomString();
    this.interaction = {
      "component_id": null,
      "text": '',
      "question": '',
      "field_id": this.generateId('question'),
      "component_type": null,
      "is_primary_rating": false,
      "is_mandatory": true,
      "is_displayed_on_dashboard": true,
      "is_displayed_on_summary": false,
      "is_displayed_emoticon_on_dashboard": false,
      "conversation_type": 'question',
      "type": "detailed-rating",
      "label": labelId,
      "possible_values": [],
      "is_new_question": true,
      "params": {},
      "hide_label": false,
      "do_not_repeat": false,
      "component_category": null,
      "style": {
        "text": {},
        "interaction": {}
      }
    }
    this.defaultInteraction = Helpers.deepClone(this.interaction)
    // reinitializeComponentsList(null);
  }

  getInteractionDetail(id) {
    return new Promise((resolve: any) => {
      if (id) {
        var similar_type_components_list = [
          'ltms-emoticon-wheel',
          'ltms-ten-pill-rater',
          'ltms-nps-emoticon-box-rater',
          'ltms-nps-fun-wheel',
          'ltms-nps-slider-rater',
          'ltms-nps-wheel',
          'ltms-emoticon-rater',
          'ltms-five-pill-rater',
          'ltms-five-point-slider-rater',
          'ltms-fun-wheel-five-point',
          'ltms-star-rater',
          'ltms-detailed-slider',
          'ltms-fun-one-wheel',
          'ltms-fun-wheel',
          'ltms-slider-rater',
          'ltms-wheel',
          'ltms-nps-five-star-rater',
          'ltms-nps-multi-format-rater'
        ];
        this.interaction = this.question//_.find(conversationFields, { "field_id": id });
        this.displayLabel = !this.interaction.hide_label

        if (this.interaction) {
          if (this.interaction.rating_bar_reactor_allowed && !this.interaction.reactor_type) {
            this.interaction.reactor_type = "ltms-rating-text-reactor";
          }

          if (this.component_rater_type_mapping[this.interaction.component_type]) {
            this.interaction.scale_rater_type = this.component_rater_type_mapping[this.interaction.component_type];
          }
          //  Grouping the similar type of components by comparing the main_rater_type
          if (similar_type_components_list.indexOf(this.interaction.component_type) > -1) {
            this.isEditInteractionType = true;
            setTimeout(() => {
              // this.components = []
              const selectedComponent = _.find(this.components, { _id: this.interaction.component_id });
              this.components = _.filter(this.components, (component: any) => {
                //Filter components in Studio Tab
                if (similar_type_components_list.indexOf(component.name) > -1 && (this.interaction.main_rater_type == component.main_rater_type && this.interaction.component_id != component._id)) {
                  return component.display_on != 'settings';
                } else {
                  return false;
                }
              });
              // if the component is not present in the interactions then fetching the selected component from componentHash
              // changed the key for selected component id - LP21031319655
              var selected_comp = this.selectedComponent || selectedComponent;
              this.components.unshift(selected_comp);
              // reinitialize the components hash for the selected component in edit mode.
              this._conversation.createHashForComponent(this.components)
              this.initializeComponent()

              resolve()
            }, 0)
          } else {
            this._conversation.createHashForComponent(this.components)
            this.initializeComponent()
            resolve()
          }

          // We are commenting the code because of calling the API inside the $timeout 08-03-2021
          // getComponentProperties(interaction.component_id, 'edit');
        } else {
          this._conversation.createHashForComponent(this.components)
          this.initializeComponent()
          resolve()
        }
      }
    })


    // $timeout(function() {
    //     var componentDivId = $('#studioComponent_' + interaction.component_id + '_Id');  
    //     if (componentDivId) {
    //         angular.element('#studioComponent_' + interaction.component_id + '_Id').triggerHandler('click');
    //         // $timeout(function() {
    //         //Utils._id('studioComponent_' + interaction.component_id + '_Id').scrollIntoView();

    //         $('#interactionsQuestionType').animate({
    //             scrollTop: (componentDivId.position().top - 100)
    //         }, 500);

    //         // }, 10);
    //     }
    // }, 0);
  }

  generateId(type) {
    return (type.toString() + "_" + (new Date()).getTime()).toString();
  }

  loadJSONEditorScript(): Promise<boolean> {
    installCspNonceForDynamicElements();

    // If already loaded, return resolved promise
    if (jsonEditorLoaded) {
      return Promise.resolve(true);
    }

    // If loading is in progress, return the existing promise
    if (jsonEditorLoadPromise) {
      return jsonEditorLoadPromise;
    }

    // Create a new promise to load the script
    jsonEditorLoadPromise = new Promise<boolean>((resolve, reject) => {
      try {
        // Check if script already exists
        if (document.getElementById('json-editor-script')) {
          // If script exists but JSONEditor is undefined, it might still be loading
          const checkInterval = setInterval(() => {
            if (typeof JSONEditor !== 'undefined') {
              clearInterval(checkInterval);
              jsonEditorLoaded = true;
              resolve(true);
            }
          }, 100);

          // Set a timeout to avoid infinite checking
          setTimeout(() => {
            clearInterval(checkInterval);
            if (!jsonEditorLoaded) {
              reject(new Error('JSONEditor failed to initialize within timeout period'));
            }
          }, 10000); // 10 second timeout

          return;
        }

        // Create script element
        const script = this.renderer.createElement('script');
        script.id = 'json-editor-script';
        applyCspNonce(script);
        script.src = 'https://cdn.jsdelivr.net/npm/@json-editor/json-editor@latest/dist/jsoneditor.min.js';

        // Set up onload and onerror handlers
        script.onload = () => {
          // console.log('JSONEditor loaded successfully');
          jsonEditorLoaded = true;
          resolve(true);
        };

        script.onerror = (err) => {
          console.error('Failed to load JSONEditor script', err);
          jsonEditorLoadPromise = null; // Reset promise so we can try again
          reject(new Error('Failed to load JSONEditor script'));
        };

        // Append script to document body
        this.renderer.appendChild(document.body, script);
      } catch (err) {
        console.error('Error in script loading process', err);
        jsonEditorLoadPromise = null;
        reject(err);
      }
    });

    return jsonEditorLoadPromise;
  }


  toggleLabelVisiblity() {
    if(this.interaction.hide_label){
      this.interaction.hide_label = false
    }else{
      this.interaction.hide_label = true
    }
    // this.displayLabel = !this.interaction.hide_label
    this.markDirty()
  }
  // Ensure JSONEditor is ready, with retry mechanism
  private ensureEditorIsReady(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // If already ready, resolve immediately
      if (this.isEditorReady && typeof JSONEditor !== 'undefined') {
        resolve();
        return;
      }

      // If retry count exceeded, reject
      if (this.retryCount >= this.maxRetries) {
        reject(new Error('Maximum retry attempts exceeded'));
        return;
      }

      // Increment retry count
      this.retryCount++;

      // Try to load JSONEditor again
      this.loadJSONEditorScript()
        .then(() => {
          this.isEditorReady = true;
          resolve();
        })
        .catch(error => {
          console.warn(`Retry attempt ${this.retryCount} failed:`, error);

          // Wait and try again
          setTimeout(() => {
            this.ensureEditorIsReady()
              .then(resolve)
              .catch(reject);
          }, 1000); // 1 second delay between retries
        });
    });
  }


  // Draw component schema using JSONSchema editor
  drawComponentSchema(schemaData: any): void {
    if (!this.componentSchemaRef?.nativeElement) {
      console.error('Component schema element not found');
      return;
    }

    try {
      // Clear previous editor
      this.resetComponentEditor();
      // Sanitize the schema
      const sanitizedSchema = this.sanitizeSchema(schemaData);
      if (!sanitizedSchema) {
        throw new Error('Invalid schema data');
      }

      // Create new editor instance
      // const options = {
      //   schema: sanitizedSchema,
      //   theme: 'bootstrap4',
      //   iconlib: 'fontawesome4',
      //   disable_edit_json: true,
      //   disable_properties: true,
      //   disable_collapse: true,
      //   show_errors: 'never',
      //   no_additional_properties: true,
      //   required_by_default: true
      // };
      var options = {
        theme: 'bootstrap3',
        iconlib: "fontawesome4",
        // Enable fetching schemas via ajax
        ajax: true,
        // The schema for the editor
        schema: sanitizedSchema.schema,
        // Seed the form with a starting value
        startval: sanitizedSchema.defaults,
        // Disable additional properties
        no_additional_properties: true,
        // Remove all "add row" buttons from arrays.
        disable_array_add: true,
        // Remove all "add row" buttons from arrays.
        disable_array_delete: true,
        disable_array_reorder: true,
        //Remove all collapse buttons from objects and arrays.
        disable_collapse: true,
        // Remove all Edit JSON buttons from objects.
        disable_edit_json: true,
        // Remove all Edit Properties buttons from objects.
        disable_properties: true,
        // Require all properties by default
        required_by_default: false,
        // Disable select2 - Default is false (Added for Reference)
        enable_select2: false,
        show_errors: 'always',
        disable_auto_increment: false,
        disabled: !this.isUserHasWriteAccess,
        allow_html: true,
        lib_dompurify: 1
      }

      this.componentPropertyEditor = new JSONEditor(
        this.componentSchemaRef.nativeElement,
        options,
        ltmsConversationDefaultProps.getSchemaOptions(schemaData)
      );
      this.componentPropertyEditor.on('ready', () => {
        if (this.componentPropertyEditor && this.componentPropertyEditor.ready) {
          let checkboxEls: any = document.querySelectorAll('[data-schematype="boolean"]>div>label');
          if (checkboxEls && checkboxEls.length) {
            checkboxEls.forEach((el: any) => {
              const textEl: any = document.createElement('div');
              textEl.innerText = el.innerText;
              const input: any = el.querySelector('input[type="checkbox"]')
              el.innerText = ''
              el.appendChild(textEl);
              el.appendChild(input);

            })
          }
          let selectionEls: any = document.querySelectorAll('#componentSchema select');
          if (selectionEls && selectionEls.length) {
            selectionEls.forEach((el: any) => {
              el.className = 'form-select'
            })
          }

          this.componentPropertyEditor.validate();

          try {
            const undefinedEls: any = document.querySelectorAll('#componentSchema select option[value="undefined"]');
            if (undefinedEls && undefinedEls.length) {
              undefinedEls.forEach((elem: any) => { if (elem) elem.remove(); });
            }

          } catch (e) {
            console.warn("Uncaught Exception occur.", e);
          }

          // Set initial values if editing
          if (this.isEditMode && this.question?.params) {
            this.componentPropertyEditor.setValue(this.question.params);
          }
          this.defaultSchemaEditorValues = Helpers.deepClone(this.componentPropertyEditor.getValue())

          // // Add change event listener
          this.componentPropertyEditor.on('change', () => {
            // this.updateComponentProperties();
            this.markDirty()
          });

          // Apply custom styling
          this.applyCustomStyling();
          if (!this.isUserHasWriteAccess && this.componentSchemaRef?.nativeElement) {
            const inputs: NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> = this.componentSchemaRef.nativeElement.querySelectorAll('input, select, textarea');
            inputs.forEach((el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => { el.disabled = true; });
          }
          setTimeout(() => {
            var currentInteraction = this.interaction;
            // Changed default value based on input type [Applied on single line text.]
            this.componentPropertyEditor?.watch('root.inputType', () => {
              setTimeout(() => {
                var inputType = this.componentPropertyEditor.editors.root.value.inputType;
                var defaultInputSchema = SchemaProvider.components.inputbox[inputType];
                if (currentInteraction && currentInteraction.params && currentInteraction.params.inputType && inputType == currentInteraction.params.inputType.toLowerCase()) {
                  defaultInputSchema = currentInteraction.params;
                }
                if (!defaultInputSchema) {
                  defaultInputSchema = SchemaProvider.components.inputbox['default'];
                  defaultInputSchema.inputType = inputType;
                }
                this.componentPropertyEditor.setValue(defaultInputSchema);
                const controllLabelEls: any = document.querySelectorAll('#componentSchema .control-label');
                if (controllLabelEls && controllLabelEls.length) {
                  controllLabelEls.forEach((elem: any) => {
                    elem.classList.add('col col-md-4 col-lg-4 col-sm-4 col-xs-4');
                  })
                }
                const formControlEls: any = document.querySelectorAll('#componentSchema .form-control');
                if (formControlEls && formControlEls.length) {
                  formControlEls.forEach((elem: any) => {
                    elem.classList.add('col width-col-8 padding-left-10');
                  })
                }
              }, 0);
            });
            // Changed default value for Multi select/single select[Based on display Type eg square, inline,block etc].
            this.componentPropertyEditor?.watch('root.displayType', () => {
              setTimeout(() => {
                // Get current selected display type
                var displayType = this.componentPropertyEditor.editors.root.value.displayType;
                // Get current schema values;
                var currentSchema = this.componentPropertyEditor.options.startval;
                // Get default schema, based on display type.
                var defaultInputSchema = SchemaProvider.components.single_multi_select_default[displayType];
                if (defaultInputSchema && currentSchema) {
                  // update values with defaults.
                  for (var key in defaultInputSchema) {
                    if (defaultInputSchema.hasOwnProperty(key)) {
                      currentSchema[key] = defaultInputSchema[key];
                    }
                  }
                  // Set display type
                  currentSchema.displayType = displayType;
                  // Update schema for selected display type.
                  this.componentPropertyEditor.setValue(currentSchema);
                }
              }, 0);
            });
          }, 0);
        }
      })





    } catch (error) {
      console.error('Error drawing component schema:', error);
      this.componentSchemaRef.nativeElement.innerHTML = `
        <div class="alert alert-danger">
          Failed to load component properties editor. Please try again.
        </div>
      `;
    }
  }

  private generateHash(obj): string {
    if (obj !== undefined && !_.isEmpty(obj)) {
      return _.map(obj, (val, key) => { return `${key}-${JSON.stringify(val)}` }).join('|'); // Simple hash function
    } else {
      return 'NOHASHED'
    }
  }

  // Get component schema for rendering
  getComponentSchema(componentId: string): void {
    if (!componentId) return;

    this.resetComponentEditor();
    this.checkComponentRaterType(componentId);

    this.settingService.getComponentSchema(componentId).subscribe(
      (response) => {
        if (response && response?.data?.code == 0 && !_.isEmpty(response.data.response)) {
          const schemaData = response.data.response[0].details;
          this.checkAndDrawSchema(schemaData);
        }
      },
      (error) => {
        console.error('Error loading component schema:', error);
      }
    );
  }

  checkAndDrawSchema(schemaData: any): void {
    // Only draw schema if editor is ready
    if (this.componentPropertyEditor) {
      this.drawComponentSchema(schemaData);
    } else {
      // Store schema data for when editor is ready
      this.pendingSchemaData = schemaData;

      // Try to ensure JSONEditor is loaded
      this.ensureEditorIsReady().then(() => {
        if (this.pendingSchemaData) {
          this.drawComponentSchema(this.pendingSchemaData);
          this.pendingSchemaData = null;
        }
      }).catch(error => {
        console.error('Failed to ensure editor is ready:', error);
      });
    }
  }


  updateComponentProperties() {
    return new Promise((resolve, reject) => {
      var isCheckboxQuestion = (this.interaction.component_category === 'multi-select');
      if (this.interaction && this.interaction.component_id) {
        var language = this._conversation.default_language;
        // get updated value from schema editor
        var updatedValue = this.getComponentPropertiesUpdatedValue();
        if (updatedValue) {
          if (isCheckboxQuestion && this.interaction.params && this.interaction.params.options) {
            updatedValue.options = this.interaction.params.options;
          }
          // update params into interaction
          this.interaction.params = updatedValue;
          // changes by Bhunesh - 20th Feb 2017 - Added number of distributions property 
          if (updatedValue.numberOfDistributions) {
            this.interaction.numberOfDistributions = updatedValue.numberOfDistributions;
          } else {
            delete this.interaction['numberOfDistributions'];
          }
        }
        if (this.interaction.params) {
          var paramsForMQL: any = {},
            fieldsForUpdation: any = {};
          if (!isCheckboxQuestion) {
            delete this.interaction['sub_questions'];
            this._conversation.setDashboardProperties(this.interaction);
            // paramsForMQL = Conversation.getParamsForMQL(interaction.params);
          } else {
            this._conversation.updateChildInteractions(this.interaction);
            // paramsForMQL = interaction.params;
          }
          this._conversation.getParamsForMQL(this.interaction.params).then((paramsForMQL) => {
            fieldsForUpdation = {
              "field_id": this.interaction.field_id,
              "read-only": this.interaction['read-only'],
              "label": this.interaction.label,
              "params": paramsForMQL,
              "component_type": this.interaction.component_type,
              "type": this.interaction.type,
              "has_reactor": this.interaction.rating_bar_reactor_allowed,
              "do_not_repeat": this.interaction.do_not_repeat,
              "conditional_label": this.interaction.conditionalLabel
            };
            // Delete label if user has selected hide label  for Interaction
            if (this.interaction && this.interaction.hide_label == true) {
              delete fieldsForUpdation['label'];
            }
            if (this.interaction.question_component_type) {
              fieldsForUpdation.question_component_type = this.interaction.question_component_type;
            }

            if (this.interaction['visible-if']) {
              fieldsForUpdation['visible-if'] = this.interaction['visible-if'];
            }

            if (this.interaction['is_mandatory_if']) {
              fieldsForUpdation['is_mandatory_if'] = this.interaction['is_mandatory_if'];
            }

            if (this.interaction['server-visible-if']) {
              fieldsForUpdation['server-visible-if'] = this.interaction['server-visible-if'];
            }

            if (this.interaction.allowMultiSelect != null) {
              fieldsForUpdation['allowMultiSelect'] = this.interaction['allowMultiSelect'];
              fieldsForUpdation['enable_shuffle'] = (this.interaction['allowMultiSelect'] && this.interaction.enable_shuffle) ? true : false;
            }
            this._conversation.updateComponentPropertiesIntoScreens(fieldsForUpdation);
            delete this.interaction['is_new_question'];
            delete this.interaction['logics'];
            resolve(true)
          })
        } else {
          delete this.interaction['is_new_question'];
          delete this.interaction['logics'];
          resolve(true)
        }


      } else {
        resolve(false)
      }
    })

  }

  // Apply custom styling to JSONEditor
  applyCustomStyling(): void {
    if (!this.componentSchemaRef?.nativeElement) return;

    // Add custom classes to the editor elements
    const editorElement = this.componentSchemaRef.nativeElement;
    const styleElement = document.createElement('style');
    applyCspNonce(styleElement);

    styleElement.textContent = `
      .json-editor {
        border: 1px solid #dee2e6;
        border-radius: 0.25rem;
        padding: 1rem;
      }
      
      .json-editor .well {
        background-color: #f8f9fa;
        border: none;
        box-shadow: none;
      }
      
      .json-editor .form-control {
        border-radius: 0.25rem;
        border: 1px solid #ced4da;
      }
      
      .json-editor .btn {
        border-radius: 0.25rem;
      }
      
      .json-editor .form-check {
        margin-bottom: 0.5rem;
      }
      
      .json-editor .form-check-input {
        margin-top: 0.25rem;
      }
    `;

    editorElement.appendChild(styleElement);
  }

  // Check if component supports specific rater types
  checkComponentRaterType(id: string): void {
    this.showCalculateMean = false;
    const component = this.components.find(c => c._id === id);

    if (component && component.main_rater_type) {
      // Show calculate mean for specific rating types (main_rater_type 3 or 10)
      if (component.main_rater_type === 3 || component.main_rater_type === 10) {
        this.showCalculateMean = true;
      }
    }
  }
  // Reset component editor
  resetComponentEditor(): void {
    this.componentPropertyEditor = null;
    if (this.componentSchemaRef && this.componentSchemaRef.nativeElement) {
      this.componentSchemaRef.nativeElement.innerHTML = '';
    }
  }

  // Sanitize schema for display (only used in fallback mode)
  sanitizeSchema(schema: any): any {
    if (!schema) return null;

    // Deep clone the schema to avoid modifying the original
    const sanitizedSchema = JSON.parse(JSON.stringify(schema));

    // Remove any potentially dangerous properties
    const removeDangerousProps = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;

      const dangerousProps = ['__proto__', 'constructor', 'prototype'];
      dangerousProps.forEach(prop => delete obj[prop]);

      Object.values(obj).forEach(value => {
        if (typeof value === 'object') {
          removeDangerousProps(value);
        }
      });
    };

    removeDangerousProps(sanitizedSchema);
    return sanitizedSchema;
  }

  isRatingComponent(): boolean {
    return !!this.selectedComponent &&
      (this.selectedComponent.type === 'rating' ||
        this.selectedComponent.category === 'rating');
  }

  isMultiSelect(): boolean {
    return !!this.selectedComponent &&
      (this.selectedComponent.component_category === 'multi-select' ||
        this.selectedComponent.name === 'ltms-multi-check-box');
  }
  getAspectRatio(currentDevice: any) {
    let ratio: string = ''
    switch (currentDevice) {
      case 'mobile':
        ratio = '4/3'
        break;
      case 'desktop':
        ratio = '16/9'
        break;
      case 'tablet':
        break;
      default:
        break;

    }
    return ratio;
  }

  // Toggle visibility of child interactions (sub-questions)
  toggleChildInteractions(): void {
    const elem: any = document.getElementById('childInteractionsList')
    if (this.selectedComponent && this.selectedComponent.component_category === 'multi-select') {
      if (elem) {
        elem.style.display = 'block';
        if (!this.interaction.sub_questions) {
          this.interaction.sub_questions = [];
        }
      } else {
        if (elem)
          elem.style.display = 'block';
      }
      // Make sure we have at least 2 sub-questions for multi-select
      // const subQuestionsArray = this.getSubQuestionsArray();
      // if (subQuestionsArray.length < 2) {
      //   // Add initial options for multi-select
      //   this.addSubQuestion();
      //   this.addSubQuestion();
      // }
    }
  }

  // Get sub-questions form array
  getSubQuestionsArray(): FormArray {
    return this.questionForm.get('subQuestions') as FormArray;
  }

  // Getter for template access
  get subQuestionsArray(): FormArray {
    return this.getSubQuestionsArray();
  }
  // Add a new sub-question
  addSubQuestion(): void {
    this.getSubQuestionsArray().push(this.createSubQuestionControl());
  }


  // Create a form control for a sub-question
  createSubQuestionControl(subQuestion?: any): FormGroup {
    return this.fb.group({
      field_id: [subQuestion?.field_id || 'sub_' + Math.random().toString(36).substring(2, 9)],
      text: [subQuestion?.text || '', Validators.required],
      is_hidden: [subQuestion?.is_hidden || false],
      enableShuffle: [subQuestion?.enable_shuffle || false],
      option_type: [subQuestion?.option_type || '']
    });
  }

  questionTypesMap: any = {}
  questionTypes: any = []
  questionComponents: any = [];

  selectedQuestionType: any = null;
  selectedComponentId: any = null;
  selectedComponent: any = null;
  conversationFieldsHash: any = {}
  // conversationFields: any = []
  components: QuestionComponent[] = [];
  loadComponentData() {
    return new Promise((resolve: any, reject: any) => {
      // TODO: remove this false  in next release
      if (false && !_.isEmpty(this._conversation.componentsHash)) {
        if (this.isEditMode && !_.isEmpty(this.question)) {
          this.components = Object.values(this._conversation.componentsHash);
          this.getInteractionDetail(this.question?.component_id).then(() => {
            resolve()
          })
        } else {
          this.initializeComponent();
          resolve()
          return;
        }

      } else {
        this.questionTypesMap = {}
        this.questionTypes = []
        this.settingService.getQuestionnaireComponents().subscribe((response) => {
          if (response?.data?.code == 0 && !_.isEmpty(response?.data?.components)) {
            this.components = response?.data?.components || [];

            if (this.isEditMode && !_.isEmpty(this.question)) {
              this.getInteractionDetail(this.question?.component_id).then(() => {
                resolve()
              })
            } else {
              this._conversation.createHashForComponent(this.components)
              this.initializeComponent()
              resolve()
            }
          } else {
            reject()
          }
        })
      }
    })

  }

  initializeComponent() {
    this.componentsHash = this._conversation.getComponentHash();
    this.questionTypesMap = this._conversation.getComponentTypes();
    this.questionTypes = Object.values(this.questionTypesMap)
    if (this.questionTypes?.length) {
      if (!this.isEditMode) {
        this.selectedQuestionType = this.questionTypesMap['nps']?.type
        this.questionComponents = this.questionTypesMap['nps']?.components
        this.selectedComponentId = this.questionComponents[0]?._id
        this.selectedComponent = this.questionComponents[0];
      } else {
        this.selectedComponentId = this.question?.component_id;
        this.selectedComponent = this.componentsHash[this.selectedComponentId]
        const { category, label } = this._conversation.getComponentCategory(this.selectedComponent);
        this.selectedQuestionType = category || 'other';
        // this.selectedQuestionType = (this.selectedQuestionType == 'rating' && (this.selectedComponent.main_rater_type == 3 || this.selectedComponent.main_rater_type == '3')) ? 'nps' : this.selectedQuestionType;
        this.questionComponents = this.questionTypesMap[this.selectedQuestionType].components
      }
    }

    this.interaction.component_category = this.selectedComponent.component_category || null;
    this.getComponentProperties(this.selectedComponentId, 'selector')
  }

  onQuestionTypeChange(questionType: any) {
    this.selectedQuestionType = questionType;
    this.questionComponents = this.questionTypesMap[questionType].components
    this.selectedComponent = this.questionComponents[0]
    this.selectedComponentId = this.selectedComponent._id
    if (!this.isEditMode) {
      this.initInteraction()
    }
    this.getComponentProperties(this.selectedComponentId, 'selector')
    if (this.activePropertyTab === 'advanced') {

      this.getComponentSchema(this.selectedComponentId)
    }
  }

  onComponentChange(componentid: any) {
    this.selectedComponentId = componentid;
    this.selectedComponent = _.find(this.questionComponents, { _id: componentid });
    if (!this.isEditMode) {
      this.initInteraction()
    }
    this.getComponentProperties(componentid, 'selector')
    if (this.activePropertyTab === 'advanced') {
      this.getComponentSchema(this.selectedComponentId)
    }
  }
  // Initialize pill values
  initializePillValues(): void {
    this.pillValues = [];
    const defaultColors = {
      color: '#777777',
      dashboardColor: '#777777',
      activeColor: '#777777'
    };

    for (let i = 0; i < this.numberOfPills; i++) {
      this.pillValues.push({
        message: (i + 1).toString(),
        color: defaultColors.color,
        dashboardColor: defaultColors.dashboardColor,
        activeColor: defaultColors.activeColor
      });
    }
  }

  // Update number of pills
  updateNumberOfPills(): void {
    const newPillCount = Math.min(Math.max(1, this.numberOfPills), 10);
    this.numberOfPills = newPillCount;

    if (newPillCount > this.pillValues.length) {
      // Add more pills
      const defaultColors = {
        color: '#777777',
        dashboardColor: '#777777',
        activeColor: '#777777'
      };

      for (let i = this.pillValues.length; i < newPillCount; i++) {
        this.pillValues.push({
          message: (i + 1).toString(),
          color: defaultColors.color,
          dashboardColor: defaultColors.dashboardColor,
          activeColor: defaultColors.activeColor
        });
      }
    } else if (newPillCount < this.pillValues.length) {
      // Remove excess pills
      this.pillValues = this.pillValues.slice(0, newPillCount);
    }
  }

  // Open color picker
  openColorPicker(type: 'default' | 'dashboard' | 'active', index: number, event: MouseEvent): void {
    // Get the current color value based on the type
    let currentColor: string;
    switch (type) {
      case 'default':
        currentColor = this.pillValues[index].color;
        break;
      case 'dashboard':
        currentColor = this.pillValues[index].dashboardColor;
        break;
      case 'active':
        currentColor = this.pillValues[index].activeColor;
        break;
    }

    // Set the active color picker
    this.activeColorPicker = { type, index };
    this.activeColorPickerValue = currentColor;

    // Position the color picker near the clicked element
    const padding = 10;
    this.colorPickerX = event.clientX + padding;
    this.colorPickerY = event.clientY + padding;

    // Check if color picker would go off screen and adjust if needed
    setTimeout(() => {
      const picker = document.querySelector('.color-picker-popup') as HTMLElement;
      if (picker) {
        const pickerRect = picker.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Check right edge
        if (this.colorPickerX + pickerRect.width > viewportWidth) {
          this.colorPickerX = viewportWidth - pickerRect.width - padding;
        }

        // Check bottom edge
        if (this.colorPickerY + pickerRect.height > viewportHeight) {
          this.colorPickerY = viewportHeight - pickerRect.height - padding;
        }
      }
    }, 0);
  }

  // Close color picker
  closeColorPicker(): void {
    this.activeColorPicker = null;
  }

  // Update active color
  updateActiveColor(): void {
    // No implementation needed, two-way binding handles the update
  }

  // Select predefined color
  selectPredefinedColor(color: string): void {
    this.activeColorPickerValue = color;
  }

  // Apply selected color
  applyColor(): void {
    if (this.activeColorPicker) {
      const { type, index } = this.activeColorPicker;

      switch (type) {
        case 'default':
          this.pillValues[index].color = this.activeColorPickerValue;
          break;
        case 'dashboard':
          this.pillValues[index].dashboardColor = this.activeColorPickerValue;
          break;
        case 'active':
          this.pillValues[index].activeColor = this.activeColorPickerValue;
          break;
      }

      this.closeColorPicker();
    }
  }

  // Generate a random color (for demo purposes - can be removed)
  getRandomColor(): string {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  // Search questions
  searchQuestions(): void {
    if (!this.searchQuery.trim()) {
      this.filteredQuestions = [...this.questionsList];
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.filteredQuestions = this.questionsList.filter(question =>
      question.text.toLowerCase().includes(query) ||
      question.type.toLowerCase().includes(query)
    );
  }

  // Clear search
  clearSearch(): void {
    this.searchQuery = '';
    this.filteredQuestions = [...this.questionsList];
  }

  // Show tooltip
  showTooltip(event: MouseEvent, question: QuestionItem): void {
    this.activeTooltip = question;

    // Calculate position
    const padding = 15;
    this.tooltipX = event.clientX + padding;
    this.tooltipY = event.clientY + padding;

    // Check if tooltip would go off screen
    setTimeout(() => {
      const tooltip = document.querySelector('.question-tooltip') as HTMLElement;
      if (tooltip) {
        const tooltipRect = tooltip.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Check right edge
        if (this.tooltipX + tooltipRect.width > viewportWidth) {
          this.tooltipX = viewportWidth - tooltipRect.width - padding;
        }

        // Check bottom edge
        if (this.tooltipY + tooltipRect.height > viewportHeight) {
          this.tooltipY = viewportHeight - tooltipRect.height - padding;
        }
      }
    }, 0);
  }

  // Hide tooltip
  hideTooltip(): void {
    this.activeTooltip = null;
  }

  // Edit question
  editQuestion(id: number): void {
    // console.log('Editing question with ID:', id);
    const question = this.questionsList.find(q => q.id === id);
    if (question) {
      // Populate form with question data
      this.questionTitle = question.text;
      this.questionType = question.type;

      // Switch to create tab
      this.setActiveTab('create');
    }

    // Hide tooltip
    this.hideTooltip();
  }

  // Duplicate question
  duplicateQuestion(id: number): void {
    // console.log('Duplicating question with ID:', id);
    const question = this.questionsList.find(q => q.id === id);
    if (question) {
      // Create a new question based on the original
      const newQuestion = {
        ...question,
        id: Math.max(...this.questionsList.map(q => q.id)) + 1,
        text: `${question.text} (Copy)`,
        isPrimary: false // Duplicates shouldn't be primary
      };

      // Add to list
      this.questionsList.push(newQuestion);
      this.filteredQuestions = [...this.questionsList];
    }

    // Hide tooltip
    this.hideTooltip();
  }

  // Tab navigation
  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  // Copy preview link to clipboard
  copyPreviewLink(): void {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = this.previewLink;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);

    // Show copy success indicator
    this.showCopySuccess = true;
    setTimeout(() => {
      this.showCopySuccess = false;
    }, 2000);
  }

  // Toggle device view in preview
  setDeviceView(device: string): void {
    this.currentDevice = device;
  }

  // Toggle properties panel
  togglePropertiesPanel(): void {
    this.isPropertiesPanelOpen = !this.isPropertiesPanelOpen;
    if (this.isPropertiesPanelOpen && this.activePropertyTab === 'advanced') {
      this.getComponentSchema(this.selectedComponentId)
    }
  }

  // Set active property tab
  setPropertyTab(tab: string): void {
    console.log('tab',tab);
    this.activePropertyTab = tab;
    if (tab === 'advanced') {
      // this.getComponentSchema(this.selectedComponentId)
    }
  }

  // Toggle display in conversation
  toggleDisplayInConversation(): void {
    this.displayInConversation = !this.displayInConversation;
  }




  shouldSave: boolean = false;
  is_displayed_on_dashboard: boolean = false;
  is_displayed_on_summary: boolean = false;
  is_displayed_emoticon_on_dashboard: boolean = false;
  saveInteraction() {
    /** Property validation for multiline text component */
    if (this.interaction && this.interaction.component_type && this.interaction.component_type === "ltms-text-area") {
      var propertyEditor = this.componentPropertyEditor.getValue();
      if (propertyEditor.minimumCharacters && propertyEditor.maximumCharacters && propertyEditor.minimumCharacters > propertyEditor.maximumCharacters) {
        return Helpers.smallAlertBox("Error", "Maximum character limit should be greater than or equal to minimum limit", "error", 2000);
      }
      if (propertyEditor.minimumCharacters && (propertyEditor.minimumCharacters < 0 || propertyEditor.minimumCharacters.toString().indexOf(".") !== -1)) {
        return Helpers.smallAlertBox("Error", "Minimum limit should be a valid number. Alphabets, special characters, decimal and negative numbers are not allowed", "error", 2000);
      }
      if (propertyEditor.maximumCharacters && (propertyEditor.maximumCharacters < 0 || propertyEditor.maximumCharacters.toString().indexOf(".") !== -1)) {
        return Helpers.smallAlertBox("Error", "Maximum limit should be a valid number. Alphabets, special characters, decimal and negative numbers are not allowed", "error", 2000);
      }

      if (propertyEditor.maximumCharacters == 0) {
        propertyEditor.maximumCharacters = 2000;
        this.componentPropertyEditor.setValue(propertyEditor);
      }
    }
    // prepare linked field list for options of the interaction
    this.prepareLinkedFieldList();
    const saveInteractionButton = document.getElementById('saveInteractionButton') as HTMLButtonElement;
    saveInteractionButton.setAttribute('disabled', 'true');
    this.shouldSave = true;
    if (this.interaction && this.validateMinmumOptionRequiredForOptions(this.interaction)) {
      /** Appending condition object */
      // interaction
      // LtmsEventListener.fire('studioInteractionSaved');//used for update the interaction condition
      this.updateCurrentCondition(this.componentConditions.logicAction);
      var type = (this.interaction.is_primary_comment || this.interaction.is_primary_rating) ? 'primary-rating' : 'detailed-rating';
      this.interaction.type = type;
      this.interaction.main_rater_type = this.selectedComponent.main_rater_type;
      this.interaction.scale_rater_type = this.selectedComponent.scale_rater_type;
      //Updating the flag is_displayed_on_dashboard using dashboard.
      this.is_displayed_on_dashboard = (this.is_displayed_on_dashboard) ? true : false;
      this.is_displayed_on_summary = this.interaction.is_displayed_on_summary || false;
      this.is_displayed_emoticon_on_dashboard = this.interaction.is_displayed_emoticon_on_dashboard || false;
      if (this.interaction.component_type == 'ltms-link' || this.interaction.component_type == "litmus-link") {
        try {
          const labelEl: any = document.getElementsByName("root[_ltmsLabelText]")[0]
          var labelText: string = ''
          if (labelEl)
            labelText = labelEl.value;
          //To get the value of link text and redirection link from the hyperlink component.
          const hrefEl: any = document.getElementsByName("root[href]")[0];
          var href: string = ''
          if (hrefEl)
            href = hrefEl.value;
          //Check for hyperlink fields ,if link text and redirection link is empty user will not allowed to save the component.
          if (this.interaction.params && ((href.length <= 0) || (labelText.length <= 0))) {
            Helpers.smallAlertBox("Error", "Link text and redirection link are required", "error", 2000);
            this.shouldSave = false;
          }
        } catch (e) {
          console.warn("Uncaught Exception occur.", e)
        }
      }
      // upadating component Params
      if ((this.interaction.field_id != 'primary_rating' && this.interaction.field_id != 'comments') && type == 'primary-rating') {
        //If question set as primary interaction or primary comment then is_displayed_on_dashboard should be true.
        this.interaction.is_displayed_on_dashboard = true;
        var msg = this.interaction.is_primary_comment ? "Primary Comment" : "Primary Question";
        this.confirmPrimaryInteraction(msg, (yes) => {
          if (!yes) {
            this.interaction.type = "detailed-rating";
            this.interaction.is_primary_comment = this.interaction.is_primary_rating = false;
            this.interaction.is_displayed_on_summary = false;
            try {
              const isPrimaryRatingChkBox = document.getElementById('isPrimaryRatingChkBox') as HTMLInputElement;
              const isPrimaryCommentChkBox = document.getElementById('isPrimaryCommentChkBox') as HTMLInputElement;
              isPrimaryRatingChkBox.checked = false;
              isPrimaryCommentChkBox.checked = false;
            } catch (e) {
              console.log("There's a problem with the question configuration");
            }
          }
          setTimeout(() => {
            this.setInteractionTypeAndId();
            if (this.shouldSave == true) {
              this.updateAndSaveInteractionProperties();
            } else {
              Helpers.smallAlertBox("Error", "Something went wrong", "error", 2000);
            }
          }, 1000);
        });
      } else {
        this.setInteractionTypeAndId();
        if (this.shouldSave == true) {
          if (this.checkMultiSelectProperties(this.interaction)) {
            this.updateAndSaveInteractionProperties();
          } else {
            console.log("There's a problem with the question configuration");
          }
        }
      }
    }
    this.isDirty = false;
  }


  getComponentProperties(componentId, caller) {
    if (caller == 'selector' || caller == 'edit') {
      this.selectedComponentId = componentId;
    }
    if (componentId) {
      this.resetComponentEditor();
      this.checkComponentRaterType(componentId);
      if (!this.interaction.component_id) {
        this.interaction.component_id = componentId;
      }
      this.settingService.getComponentSchema(componentId).subscribe((response: any) => {
        if (response && _.get(response, 'data.code') == 403) {
          Helpers.showAuthorizationErr();
        } else {
          if (response && _.hasIn(response, 'data.response') && !_.isEmpty(response?.data?.response)) {
            this.selectedComponent = Helpers.deepClone(this._conversation.componentsHash[componentId]);

            this.interaction.component_category = this.selectedComponent.component_category || null;

            if (!(caller == 'edit')) {
              this.interaction.component_type = this.selectedComponent.name;
              this.interaction.component_id = this.selectedComponent._id;
              if (this.selectedComponent.main_rater_type) {
                this.interaction.main_rater_type = this.selectedComponent.main_rater_type
              } else {
                delete this.interaction.main_rater_type;
              }

              if (this.isEditMode && this.interaction.component_type == 'litmus-text-label') {
                this.activePropertyTab = 'advanced'
              }
            }

            this.toggleChildInteractions();

            // reinitializeComponentsList(interaction.component_id);

            if (!_.isEmpty(this.interaction.params) && (caller == 'edit' || this.interaction.old_component_id == componentId)) {
              if (!_.isEmpty(response?.data?.response[0].details.defaults)) {
                var defaultProps = response?.data?.response[0].details.defaults;
                for (var prop in defaultProps) {
                  if (!(prop in this.interaction.params)) {
                    this.interaction.params[prop] = defaultProps[prop];
                  }
                  if (prop == 'configuration') {
                    try {
                      if (this.interaction.params.configuration && this.interaction.params.configuration.length) {
                        this.interaction.params.configuration = _.merge(defaultProps.configuration, this.interaction.params.configuration);
                      }
                    } catch (e) {

                    }
                  }
                }
              }
              response.data.response[0].details.defaults = this.interaction.params;
            }


            this.checkAndDrawSchema(response?.data?.response[0].details);


            if (this.interaction.component_category === 'multi-select') {
              // Set default value if allow multi select.
              if ((this.interaction['component_type'] == "litmus-text-label") || (this.interaction['component_id'] == '5abb20ff66959147c138135a')) {
                this.interaction.allowMultiSelect = true;
              } else {
                this.interaction.allowMultiSelect = (this.interaction.allowMultiSelect) ? true : false;
              }
              this.interaction.sub_questions = this._conversation.getFieldsByGroupId(this.interaction.field_id);
              /**
               * Task: #10786 (server-visible-if)
               * server visible condition not showing in accelerate for projects were created in LW-1
               * if key "setupOptions": true/false (this key & value is part of accelerate) so below check statement will not work. 
               * if key "setupOptions" not exist & multiSelect allowed & check if any 'server-visible-if' condition found against options
               */
              if ((this.interaction['component_type'] == 'litmus-text-label' || this.interaction['component_type'] == 'ltms-multi-check-box') && this.interaction['setupOptions'] == undefined && this.interaction.allowMultiSelect && this.interaction.sub_questions && this.interaction.sub_questions.length) {
                for (var i = 0; i < this.interaction.sub_questions.length; i++) {
                  var obj = this.interaction.sub_questions[i];
                  if (('server-visible-if' in obj && obj['server-visible-if'] !== "") || ('visible-if' in obj && obj['visible-if'] !== "")) {
                    this.interaction.setupOptions = true;
                    break;
                  }
                }
              }
              // prepare the multi option multi select data and initialize the same
              this.getSelectedLinkedFieldList(this.interaction);
            }
            //Set default value for allowMultiSelect = false for Multi option Single Select - new component.
            if (this.interaction.component_id == '5b5eb20f40adb9549f9d6832') {
              this.interaction.allowMultiSelect = false;
            }

            // initializing 
            this.interaction.tagsGroup = "access_group";
            if (this.interaction.isDynamicPropertiesEnabled) {
              this.initializeTagsInput("access_group", true);
            }

            if (this.interaction.is_mandatory_if && this.interaction.is_mandatory_if.trim().length) {
              this.interaction.is_mandatory = true;
            }

            if (!this.isEditMode) {
              this.defaultInteraction = Helpers.deepClone(_.merge(this.defaultInteraction, this.interaction))
            } else {
              this.defaultInteraction = Helpers.deepClone(this.interaction);
            }
            // $timeout(function() {
            //     $rootScope.$broadcast('event-rebind-condition', true);
            // }, 100);

          } else {
            Helpers.smallAlertBox("Error", "Something went wrong", "error", 2000);
          }
        }
      });
    }
  }

  initializeTagsInput(tagGroup, flag?: any) {
    if (this.interaction && this.isDynamicPropertiesEnabled) {
      var defaults = [];
      if (!this.brandDetails.dynamic_properties) {
        this.brandDetails.dynamic_properties = {};
      }
      var tagsList = this.brandDetails.dynamic_properties[tagGroup] ? this.brandDetails.dynamic_properties[tagGroup] : [];
      if (tagsList.length == 0) {
        this.brandDetails.dynamic_properties[tagGroup] = [];
      }
      if (!this.interaction.dynamic_properties) {
        this.interaction.dynamic_properties = {};
      }
      if (this.interaction.dynamic_properties) {
        _.each(this.dynamicPropertiesGroups, (tag) => {
          if (this._conversation._id('questionTags_' + tag.value)) {
            if (this.interaction.dynamic_properties[tag.value]) {
              defaults = this.interaction.dynamic_properties[tag.value];
            } else {
              this.interaction.dynamic_properties[tag.value] = [];
              defaults = [];
            }
            // $('#questionTags_' + tag.value).tagsinput('destroy');
            // $('#questionTags_' + tag.value).tagsinput('refresh');

            Helpers.initQuestionTagsInput('questionTags_' + tag.value, { defaults: defaults, tags_list: tagsList, label_field: "name", value_field: "id" });
          }
        })
      }
    }
  }

  /**
       * @description this method will identifies the interaction and selects the model value and 
       * model data for the multiple select component 
       * @param {object} selected_interaction 
       */
  getSelectedLinkedFieldList(selected_interaction) {
    var selected_options = this._conversation.multiOptionsList;
    var defaultSelected_model = this._conversation.multiSelectModel;
    if (!selected_options[selected_interaction.field_id]) {
      selected_options[selected_interaction.field_id] = selected_options['default_options'];
      defaultSelected_model[selected_interaction.field_id] = {};
    }
    this.multiSelectData = selected_options[selected_interaction.field_id];
    this.multiSelectModel = defaultSelected_model[selected_interaction.field_id];
  }

  checkMultiSelectProperties(interaction) {
    if (interaction.component_type === "ltms-multi-select") {
      var isAllFound = false;
      var isNoneFound = false;
      var isRemoveAll = false;
      var isRemoveNone = false;

      var updatedValue = this.getComponentPropertiesUpdatedValue();
      if (updatedValue) {
        interaction.params = updatedValue;
        this.interaction.params = updatedValue;
        this.componentPropertyEditor.setValue(interaction.params);
      }
      for (var i = 0, len = interaction.sub_questions.length; i < len; i++) {
        var question = interaction.sub_questions[i];
        if (question.hasOwnProperty('option_type')) {
          if (question.option_type === 'all') {
            isAllFound = true;
            if (interaction.params.hasAllOfTheAboveOption) {
              /** Update question text and labelId */
              question.text = updatedValue._ltmsLabelTextForAllTheAboveOption;
              question.labelId = this.getLabelId(question.text);
            } else {
              /** Remove option */
              isRemoveAll = true;
            }
          }
          if (question.option_type === 'none') {
            isNoneFound = true;
            if (interaction.params.hasNoneOfTheAboveOption) {
              /** Update question text and labelId */
              question.text = updatedValue._ltmsLabelTextForNoneOfTheAboveOption;
              question.labelId = this.getLabelId(question.text);
            } else {
              /** Remove option */
              isRemoveNone = true;
            }
          }
        }
      }

      var allIndex = this.findOptionTypeIndex(interaction, 'all');
      var indexOfNone = this.findOptionTypeIndex(interaction, 'none');
      var count = interaction.sub_questions.length;
      if (allIndex !== -1) {
        count = indexOfNone !== -1 ? count - 2 : count - 1;
      } else if (indexOfNone !== -1) {
        count -= 1;
      }

      if (count === 0) {
        Helpers.smallAlertBox("Error", "Options have not been configured for this interaction. Kindly complete the addition of options in the Properties section. If you Save without any \"Option\" configuration then \"All of the above\" and \"None of the above\"  will not be configured as options.", "error", 6000);
        const saveInteractionButton = document.getElementById('saveInteractionButton') as HTMLButtonElement;
        saveInteractionButton.removeAttribute('disabled');
        return false;
      }

      /**
       * Uncheck if hasAllOfTheAboveOption to false if the max-selection less than the
       * number of options
       */
      if (interaction.params.maxSelection && interaction.params.maxSelection < count) {
        interaction.params.hasAllOfTheAboveOption = false;
        this.interaction.params.hasAllOfTheAboveOption = false;
        this.componentPropertyEditor.setValue(this.interaction.params);
        if (allIndex !== -1) {
          isRemoveAll = true;
        }
      }

      /** Uncheck hasNoneOfTheAboveOption to false if the min-selection more than 1 */
      if (interaction.params.minSelection && interaction.params.minSelection > 1) {
        interaction.params.hasNoneOfTheAboveOption = false;
        this.interaction.params.hasNoneOfTheAboveOption = false;
        this.componentPropertyEditor.setValue(this.interaction.params);
        if (indexOfNone !== -1) {
          isRemoveNone = true;
        }
      }

      if (isRemoveAll) {
        this.removeSubQuestionFromInteraction(allIndex, interaction.sub_questions[allIndex].field_id);
      }

      if (isRemoveNone) {
        var indexOfNone = this.findOptionTypeIndex(interaction, 'none');
        this.removeSubQuestionFromInteraction(indexOfNone, interaction.sub_questions[indexOfNone].field_id);
      }

      if (isAllFound === false && interaction.params.hasAllOfTheAboveOption) {
        this.addSubQuestionForInteraction(interaction.params._ltmsLabelTextForAllTheAboveOption, 'all', true);
      }

      if (isNoneFound === false && interaction.params.hasNoneOfTheAboveOption) {
        this.addSubQuestionForInteraction(interaction.params._ltmsLabelTextForNoneOfTheAboveOption, 'none', true);
      }

      this.reArrangeOption(interaction);
    }

    return true;
  }

  getComponentPropertiesUpdatedValue() {
    if (this.componentPropertyEditor) {
      return this.componentPropertyEditor.getValue()
    }

    return;
  };

  getLabelId(text) {
    var existingLabelForText = this._conversation.getPropertyNameForText(this._conversation.default_language, text);
    if (!existingLabelForText) {
      var labelId = this._conversation.generateId('label') + Helpers.getRandomString();
      this._conversation.setLabelById(this._conversation.default_language, labelId, text);
      return labelId;
    }
    return existingLabelForText;
  }

  findOptionTypeIndex(interaction, optionType) {
    return _.findIndex(interaction.sub_questions, { option_type: optionType });
  }

  removeSubQuestionFromInteraction($index, fieldId, $event?: any) {
    if ($event) {
      $event.preventDefault();
    }

    var errorMsg = "";
    var isConditionApplied = false;
    var conversationField = _.find(this.conversationFields, function (convoField) {
      return convoField.field_id == fieldId;
    });

    // if (conversationField) {
    // if (conversationField.is_mandatory_if && (conversationField.is_mandatory_if.length > 1)) {
    // var options = _.filter(conversationFields, function (field) {
    //     if (field.group_id && field.group_id == fieldId) {
    //         return true;
    //     } else {
    //         return false;
    //     }
    // });
    // for (var index = 0; index < options.length; index++) {
    //     if (options['visible-if'] && options['visible-if'].length > -1) {
    //         isConditionApplied = true;
    //         break;
    //     }                                 
    // }

    // isConditionApplied = true;
    // if (fieldObj.interaction_type && fieldObj.interaction_type == "multi-select") {
    //     isConditionApplied = false;
    //     var options = _.filter(conversationFields, function (field) {
    //         if (field.group_id && field.group_id == fieldId) {
    //             return false;
    //         } else {
    //             if (field.field_id != fieldId) {
    //                 return true;
    //             } 
    //             return false;
    //         }
    //     });
    //     for (var index = 0; index < options.length; index++) {
    //         if (fieldObj.is_mandatory_if.indexOf(options[index].field_id) > -1) {
    //             isConditionApplied = true;
    //             break;
    //         }                                 
    //     }                            
    // }



    // _.each(conversationFields, function (convoField) {
    //     if (convoField.is_mandatory_if && (convoField.is_mandatory_if.indexOf(conversationField.field_id) > -1)) {
    //         if (convoField.group_id && conversationField.group_id && convoField.group_id != conversationField.group_id) {
    //             isConditionApplied = true;
    //             return;
    //         }
    //     }
    // });
    // } 
    // if (conversationField['visible-if'] && (conversationField['visible-if'].length > 1)) {
    //     isConditionApplied = true;
    // } 
    // if (conversationField['server-visible-if'] && (conversationField['server-visible-if'].length > 1)) {
    //     isConditionApplied = true;
    // }
    //     if (isConditionApplied) {
    //         errorMsg = "There are conditions/escalations associated with this interaction.";
    //     }
    // }

    if (!isConditionApplied) {
      for (var i = 0; i <= this.conversationFields.length; i++) {
        if (this.conversationFields[i] && this.conversationFields[i].field_id && this.conversationFields[i].field_id != fieldId) {
          if (this.conversationFields[i].is_mandatory_if && (this.conversationFields[i].is_mandatory_if.length > 1)) {
            //|| (conversationField.group_id != conversationFields[i].field_id)
            if ((this.conversationFields[i].group_id && this.conversationFields[i].group_id != conversationField.group_id)) {
              if (this.conversationFields[i].is_mandatory_if.search(fieldId) > 0) isConditionApplied = true;
            }
          }
          if (this.conversationFields[i]['visible-if'] && (this.conversationFields[i]['visible-if'].length > 1)) {
            if (this.conversationFields[i]['visible-if'].search(fieldId) > 0) isConditionApplied = true;
          }
          // if (conversationFields[i]['server-visible-if'] && (conversationFields[i]['server-visible-if'].length > 1)) {
          //     if (conversationFields[i]['server-visible-if'].search(fieldId) > 0) isConditionApplied = true;
          // }
          if (isConditionApplied) {
            errorMsg = "This is a conditional interaction of another interaction.";
          }
        }
      }
    }

    if (isConditionApplied) {
      $.SmartMessageBox({
        title: 'Warning!',
        content: errorMsg,
        buttons: [{
          text: "Okay",
          type: 'primary',
          key: 'Okay'
        }]
      }, function (ButtonPressed) {
      });
    } else {
      if (this.interaction && this.interaction.sub_questions) {
        this.interaction.sub_questions.splice($index, 1);
      }

      for (var i = 0; i <= this.conversationFields.length; i++) {
        if (this.conversationFields[i] && this.conversationFields[i].field_id) {
          if (this.conversationFields[i].field_id == fieldId) {
            this.conversationFields[i].is_deleted = true;
            break;
          }
        }
      }
    }
  }

  addSubQuestionForInteraction(text?: any, optionType?: any, isOptionHidden?: any) {
    if (this.interaction && this.validateEmptyOptions(this.interaction)) {
      if (!this.interaction.prop) {
        this.interaction.prop = {};
      }

      if (!this.interaction.params["options"]) {
        this.interaction.params["options"] = [];
      }

      var option: any = {
        'text': text || '',
        "group_id": this.interaction.field_id,
        "group_text": this.interaction.text + " (" + this.interaction.field_id + ")",
        "field_id": this._conversation.generateId('question') + Helpers.getRandomString(),
        "labelId": this._conversation.generateId('label') + Helpers.getRandomString(),
        "value": this.interaction.sub_questions.length + 1,
        "type": "detailed-rating",
        "option_type": optionType,
        "is_option_hidden": isOptionHidden
      }

      // while creating new option for a multi select component and the parent child linkage is enabled 
      if (this.interaction && this.interaction.enableParentChildLinkage) {
        if (!this.multiSelectModel[this.interaction.field_id]) {
          this.multiSelectModel[this.interaction.field_id] = {};
          this.multiSelectModel[this.interaction.field_id][option.field_id] = [];
        } else {
          this.multiSelectModel[this.interaction.field_id][option.field_id] = [];
        }
      }

      if (this.interaction && this.interaction.sub_questions) {
        if (this.interaction.sub_questions.length > 0) {
          var value = this.interaction.sub_questions.length + 1;
          // Avoiding duplicate values
          try {
            // pluck all values from array and get max value
            value = _.max(_.pluck(this.interaction.sub_questions, "value")) + 1;
          } catch (e) {
            value = this.interaction.sub_questions.length + 1;
          }
          option.value = value;
        }
        // fix for adding question type  - For Multi/Single Select Questions
        if (this.interaction.allowMultiSelect) {
          option.component_type = this.selectedComponent.name;
          option.interaction_type = 'multi-select';
        } else {
          option.interaction_type = 'single-select';
        }
        this.conversationFields.push(option);
        this.interaction.sub_questions.push(option);
      }
    }
  }
  /**
     * [validateEmptyOptions -  To validate the empty text]
     * @param  {[type]} interaction [Current selected in interaction]
     * @return boolean             [return true/false]
     */
  validateEmptyOptions(interaction) {
    var is_not_empty = true;
    if (interaction.sub_questions) {
      for (const res of interaction.sub_questions) {
        if (res.text.length < 1) {
          is_not_empty = false;
          Helpers.smallAlertBox("Error", "Option text is required.", "error", 2000);
          return false;
        }
      }
    }
    return is_not_empty;
  }

  reArrangeOption(interaction) {
    var allObject: any = {};
    var noneObject: any = {};
    var indexOfAll = this.findOptionTypeIndex(interaction, 'all');

    if (indexOfAll != -1) {
      allObject = interaction.sub_questions[indexOfAll];
      interaction.sub_questions.splice(indexOfAll, 1);
      interaction.sub_questions.splice(interaction.sub_questions.length, 0, allObject);

      var idx = _.findIndex(this.conversationFields, { field_id: allObject.field_id });
      if (idx !== -1) {
        var __allObject = this.conversationFields[idx];
        this.conversationFields.splice(idx, 1);
        this.conversationFields.splice(this.conversationFields.length, 0, __allObject);
      }
    }

    var indexOfNone = this.findOptionTypeIndex(interaction, 'none');
    if (indexOfNone != -1) {
      noneObject = interaction.sub_questions[indexOfNone];
      interaction.sub_questions.splice(indexOfNone, 1);
      interaction.sub_questions.splice(interaction.sub_questions.length, 0, noneObject);

      var idx = _.findIndex(this.conversationFields, { field_id: noneObject.field_id });
      if (idx !== -1) {
        var __noneObject = this.conversationFields[idx];
        this.conversationFields.splice(idx, 1);
        this.conversationFields.splice(this.conversationFields.length, 0, __noneObject);
      }
    }
  }
  updateAndSaveInteractionProperties() {
    if (this.interaction.component_id == this.selectedComponent._id && this.interaction.rating_bar_reactor_allowed) {
      this.interaction.rating_bar_reactor_allowed = this.selectedComponent.rating_bar_reactor_allowed;
    } else {
      this.interaction.rating_bar_reactor_allowed = false;
      delete this.interaction['reactor_type'];
    }

    if (this.selectedComponent.name != 'litmus-text-label' || this.selectedComponent.component_category != 'multi-select') {
      this.interaction.component_type = this.selectedComponent.name;
    }

    if (this.interaction.component_category == 'multi-select') {
      if (this.interaction.allowMultiSelect) {
        this.interaction.interaction_type = "multi-select";
        this.interaction.component_type = this.selectedComponent.name;
        this.interaction.question_component_type = 'ltms-multi-check-box';
        this.interaction.read_only = true;
        if ((this.interaction.component_type == 'litmus-text-label' || this.interaction.component_type == 'ltms-multi-check-box') && this.interaction.setupOptions == undefined) {
          this.interaction.setupOptions = false;
        }
        if (this.interaction.is_mandatory) {
          this.prepareMandatoryConditions(this.interaction);
        } else {
          this.removeMandatoryOptionCondition(this.interaction);
        }

        this.interaction.is_mandatory = false;
      } else {

        if (this.interaction.component_category == 'multi-select') {
          this.interaction.interaction_type = "single-select";
          this.interaction.component_type = this.selectedComponent.name;

          delete this.interaction["question_component_type"];
          delete this.interaction["read_only"];
          // delete interaction['is_mandatory'];
          this.removeMandatoryOptionCondition(this.interaction);
        }
      }

    } else {
      if (this.interaction.component_type == 'litmus-text-label' || this.interaction.component_type == 'litmus-image') {
        this.interaction.read_only = true;
        this.interaction.is_mandatory = false;
      }
      this._conversation.removeSubQuestionsWithGroupId();
    }

    this.interaction.question = this.interaction.question && this.interaction.question.trim().length ? this.interaction.question : this.interaction.text;

    this.interaction.old_component_id = this.interaction.component_id;

    delete this.interaction['is_new_question'];

    this.queryBuilder.saveSkipLogic(this.interaction);

    if (this.interaction.is_primary_rating || this.interaction.is_primary_comment) {
      if (this.interaction.field_id == "primary_rating" || this.interaction.field_id == "comments") {
        if (this.interaction.dynamic_properties) {
          if (this.interaction.dynamic_properties["access_group"] && this.interaction.dynamic_properties["access_group"].length) {
            this.interaction.dynamic_properties["access_group"] = [];
          }
        }
      }
    }
    // update label  on languages
    var interactionText = this.interaction.text;
    if (this.interaction.rich_text_enabled) {
      var isRichText = this.endsWith(this.interaction.label, '_rich');
      if (!isRichText) {
        this.interaction.label += '_rich';
      }
      interactionText = this.richTextService.getHtml(this.interaction.rich_text);
    }
    this._conversation.setLabelById(this._conversation.default_language, this.interaction.label, interactionText);
    this.updateComponentProperties();

    if (this.interaction.component_category) {
      delete this.interaction.component_category;
    }

    if (!this._conversation.selectedId) {
      this.conversationFields.push(this.interaction);
    }


    this.conversationFieldsHash[this.interaction.field_id] = this.interaction;

    this._conversation.getParamsForMQL(this.interaction.params).then((params) => {
      //Added to avoid duplicate entries in conversationFieldsHash ahen the question type is 'primary-rating'.
      if (this.interaction.field_id && this.interaction.type == 'primary-rating' && this.interaction.old_field_id && this.interaction.old_field_id != this.interaction.field_id) {
        this.updatePageDetailByQuestionId(params);
        const index = _.findIndex(this.conversationFields, { field_id: this.interaction.old_field_id });
        if (index != -1) {
          this.conversationFields[index] = this.interaction;
        }
        delete this.conversationFieldsHash[this.interaction.old_field_id];
      }
      if (this.interaction && this.interaction.sub_questions && this.interaction.sub_questions.length) {

        let interaction_sub_questions_map: any = {};
        _.each(this.interaction.sub_questions, (q) => {
          interaction_sub_questions_map[q.field_id] = q;
          this.conversationFieldsHash[q.field_id] = q;
        })
        this.conversationFields = this.conversationFields.map((field) => {
          if (this.interaction.field_id == field.group_id && interaction_sub_questions_map[field.field_id]) {
            // field.text = interaction_sub_questions_map[field.field_id]["text"];
            field = interaction_sub_questions_map[field.field_id];
          } else if (this.interaction.field_id == field.field_id) {
            field = this.interaction;
          }

          return field;
        })
      } else {
        this.conversationFields = this.conversationFields.map((field) => {
          if (this.interaction.field_id == field.field_id) {
            field = this.interaction;
          }

          return field;
        })
      }

      this._conversation.configuration.fields = this.conversationFields;

      this._conversation.fieldsHash = this.conversationFieldsHash;



      this._conversation.saveConversation(false, (this._conversation.selectedId) ? "Interaction Updated" : "Interaction Saved").then(() => {
        this._conversation.selectedId = null;
        // if (!this.isEditMode) {
        //   this.router.navigate([], {
        //     relativeTo: this.route,
        //     queryParams: { active_tab: 'questions' },
        //     queryParamsHandling: 'merge'
        //   });
        // }
        this.closeQuestionModal();

      });
    })

  }

  updatePageDetailByQuestionId(params: any) {
    try {
      var screens = Helpers.deepClone(this.configuration.screens[this._conversation.default_device]);
      if (screens) {
        // iterating each languages screen
        _.each(screens, (languages, language_key) => {
          //  iterating each screen
          _.each(languages, (pages, screen_key) => {
            //  iterating each fields
            _.each(pages.fields, async (fieldObj, field_key) => {
              if (fieldObj.field_id == this.interaction.old_field_id) {

                var field: any = {
                  "field_id": this.interaction.field_id,
                  "read-only": this.interaction['read-only'],
                  "label": this.interaction.label,
                  "params": params,//await this._conversation.getParamsForMQL(this.interaction.params),
                  "component_type": this.interaction.component_type,
                  "type": this.interaction.type,
                  "has_reactor": this.interaction.rating_bar_reactor_allowed
                }
                if (this.interaction && this.interaction.hide_label == true) {
                  delete field['label'];
                }
                if (this.interaction.question_component_type) {
                  field.question_component_type = this.interaction.question_component_type;
                }
                if (this.interaction['visible-if']) {
                  field['visible-if'] = this.interaction['visible-if'];
                }
                if (this.interaction['is_mandatory_if']) {
                  field['is_mandatory_if'] = this.interaction['is_mandatory_if'];
                }
                if (this.interaction['server-visible-if']) {
                  field['server-visible-if'] = this.interaction['server-visible-if'];
                }
                if (this.interaction.allowMultiSelect != null) {
                  field['allowMultiSelect'] = this.interaction['allowMultiSelect'];
                  field['enable_shuffle'] = (this.interaction['allowMultiSelect'] && this.interaction.enable_shuffle) ? true : false;
                }
                this._conversation.configuration.screens[this._conversation.default_device][language_key][screen_key]['fields'][field_key] = field;
                return;
              }
            })
          })
        });
      }
    } catch (e) { }
  }



  prepareMandatoryConditions(interaction: any) {
    if (!_.get(interaction, "logicDetail.2")) {
      if (interaction.sub_questions && interaction.sub_questions.length) {
        var expression = "";
        interaction.sub_questions.forEach((sq, index) => {
          if (sq.value != null) {
            var qEx = 'rating.detailed_rating[\'' + sq.field_id.toString() + '\']!=' + sq.value;
            expression += qEx;

            if ((interaction.sub_questions.length - index) > 1) {
              expression += '&&';
            }
          }
        });
        interaction.is_mandatory_if = expression;
      }
    }
  }

  removeMandatoryOptionCondition(interaction) {
    if (_.get(interaction, 'is_mandatory_if') && _.get(interaction, 'sub_questions')) {
      _.each(interaction.sub_questions, (subQuestion, index) => {

        var conditionExpression = '';
        if (index != 0) {
          conditionExpression += '&&'
        }
        conditionExpression += "rating.detailed_rating['" + subQuestion.field_id + "']!=" + _.get(subQuestion, 'value');
        interaction.is_mandatory_if = interaction.is_mandatory_if.replace(conditionExpression, "");
      });
    }

    if (_.get(interaction, 'is_mandatory_if') == "") {
      delete interaction['is_mandatory_if'];
    }
  }



  endsWith(str, suffix) {
    return str.slice(-suffix.length) === suffix
  }
  confirmPrimaryInteraction(msg, cb) {
    Helpers.showConfirmationBox(msg, "Are you sure, you want to set this Interaction as " + msg + "?", function (answer) {
      if (answer) {
        cb(answer)
      } else {
        cb(false);
      }
    });
  }

  toggelIsUserInfo(event: any, interaction: any) {
    console.log('event',event);
    console.log('interaction',interaction);
    if(interaction.is_user_info){
      interaction.user_info_type = '';
    }else{
      delete interaction.user_info_type;
    }
    this.markDirty();
  }

  setInteractionTypeAndId() {

    //TODO - Update read-only property.

    // if (interaction.conversation_type == 'question') {
    //     interaction['read-only'] = false;
    // } else {
    //     interaction['read-only'] = true;
    // }

    if (this.interaction.type == 'primary-rating') {
      // keep old field_id since page fields is not updated yet
      this.interaction.old_field_id = this.interaction.field_id;
      this.interaction.field_id = (this.interaction.is_primary_rating) ? 'primary_rating' : 'comments';
    } else {
      if (this.interaction.field_id == "primary_rating" || this.interaction.field_id == "comments") {
        this.interaction.field_id = this.interaction.old_field_id;
        this.interaction.old_field_id = this.interaction.field_id;
      }
    }

    if (this.interaction.component && this.interaction.component.component_category === 'text') {
      this.interaction.value_type = 'text';
    } else {
      delete this.interaction['value_type'];
    }

    delete this.interaction['sub_type'];

    // validation for field_id (used for user info)
    if (this.interaction.is_user_info) {
      if (!this.interaction.user_info_type) {
        Helpers.smallAlertBox("Error", "Please Select Type of User Information", "error", 2000);
        return;
      } else {
        var available = this.checkForFieldIdAvailibility(this.interaction.user_info_type);
        if (available) {
          this.interaction.old_field_id = this.interaction.field_id;
          this.interaction.field_id = this.interaction.user_info_type;
          this.interaction.type = "user-info";
        } else {
          delete this.interaction.is_user_info;
          delete this.interaction.user_info_type;
          return;
        }
      }
    } else {
      if (this.interaction.user_info_type) {
        delete this.interaction["user_info_type"];
        this.interaction.field_id = this.interaction.old_field_id;
        this.interaction.old_field_id = this.interaction.field_id;
      }
    }

    if (!this.selectedComponent.component_category && this.selectedComponent.component_category != 'multi-select') {
      this.interaction.component_type = this.selectedComponent.name;
      delete this.interaction['question_component_type'];
    }

    this.interaction.interaction_type = (this.selectedComponent && this.selectedComponent.type) ? this.selectedComponent.type : "rating";

    // Adding subtype - for Dashboard usage - Only for rating type components
    if (this.selectedComponent.sub_type) {
      this.interaction.sub_type = this.selectedComponent.sub_type;
    }
  }
  checkForFieldIdAvailibility(userInfoTypeId) {
    if (this.configuration?.fields && this.configuration?.fields?.length > 1) {
      var isFieldIdAvailable = _.filter(this.configuration.fields, (question_item) => {
        return (question_item.field_id != this.question?.field_id) && (question_item.field_id == userInfoTypeId);
      });
      if (isFieldIdAvailable.length >= 1 && !(isFieldIdAvailable[0].field_id == this.interaction.field_id)) {
        const userInformationType = document.getElementById('userInformationType') as HTMLInputElement;
        userInformationType.classList.add('error');
        Helpers.smallAlertBox("Error", "Selected User Information type is already being used by other Interaction.", "error", 2000);
        setTimeout(function () { userInformationType.classList.remove('error'); }, 2000);
        this.shouldSave = false;
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  /**
      * [validateMinmumOptionRequiredForOptions To validate the minimum option for multi/single select.]
      * @param  {[type]} interaction [Current selected in interaction]
      * @return boolean             [return true/false]
      */
  validateMinmumOptionRequiredForOptions(interaction): boolean | void {
    let has_options: boolean = true;
    if (!interaction.text) {
      Helpers.smallAlertBox("Error", "Please provide interaction display text.", "error", 2000);
      this.isQuestionTextAvailable = false
      // const studioConfiguratorInteractionsTab = document.querySelector('#studioConfiguratorInteractionsTab > li[value=2] > a') as HTMLAnchorElement;
      // studioConfiguratorInteractionsTab.click();
      // const saveInteractionButton = document.getElementById('saveInteractionButton') as HTMLButtonElement;
      // saveInteractionButton.removeAttribute('disabled');
      return false;
    } else {
      this.isQuestionTextAvailable = true;
    }
    if (interaction.sub_questions && interaction.sub_questions.length < 1) {
      has_options = false;
      Helpers.smallAlertBox("Error", "Options have not been configured for this interaction. Kindly complete the addition of options in the Properties section", "error", 5000);
      const saveInteractionButton = document.getElementById('saveInteractionButton') as HTMLButtonElement;
      saveInteractionButton.removeAttribute('disabled');
      return false;
    }
    if (interaction.rich_text_enabled) {
      if (!interaction.rich_text || interaction.rich_text === "") {
        Helpers.smallAlertBox("Error", "Please provide interaction text.", "error", 2000);
        const saveInteractionButton = document.getElementById('saveInteractionButton') as HTMLButtonElement;
        saveInteractionButton.removeAttribute('disabled');
        return false;
      }
    }
    if (has_options) {
      return has_options;
    }
  }

  toggleHilight($event) {
    if (this.interaction?.rich_text_enabled) {
      if (this.interaction.rich_text && this.interaction.rich_text != "") {
        this.isQuestionTextAvailable = true;
      } else {
        this.isQuestionTextAvailable = false;
      }
    } else {
      if (this.interaction.text && this.interaction.text != "") {
        this.isQuestionTextAvailable = true;
      } else {
        this.isQuestionTextAvailable = false;
      }
    }

  }

  contentChanged(data: any) {
    this.interaction.text = data?.text?.trim() || '';
    this.updateGroupTextInSubQuestion()
  }

  /**
       * @description : prepare the linked field list for save
       * this method will convert the linked field model to an array of fields 
       * and after converting the array structure would be like [field_id1,field_id2 ..... field_idn]
       */
  prepareLinkedFieldList() {
    if (this.interaction.component_type && (this.interaction.component_type === "ltms-multi-check-box" || this.interaction.component_type === "ltms-multi-select")) {
      for (var key1 in this.interaction.sub_questions) {
        if (Object.hasOwnProperty.call(this.interaction.sub_questions, key1)) {
          var element = this.interaction.sub_questions[key1];
          if (this.multiSelectModel[element.field_id] && this.multiSelectModel[element.field_id].length) {
            var linked_fields: any = this.multiSelectModel[element.field_id];
            var linked_field_ids: any = [];
            if (linked_fields.length) {
              for (var key2 in linked_fields) {
                if (Object.hasOwnProperty.call(linked_fields, key2)) {
                  var linked_field: any = linked_fields[key2];
                  if (linked_field.id && linked_field_ids.indexOf(linked_field.id) == -1)
                    linked_field_ids.push(linked_field.id);
                }
              }
              // for the current dependencies, if there is only one value in linked_field_id  
              // then we are saving only the string value else the array of linked field ids.
              this.interaction.sub_questions[key1]['linked_field_id'] = linked_field_ids.length > 1 ? linked_field_ids : linked_field_ids.toString();
            }
          } else {
            // if someone unchecked the option delete the linked_field_id
            if ('linked_field_id' in this.interaction.sub_questions[key1]) {
              delete this.interaction.sub_questions[key1].linked_field_id;
            }
          }
        }
      }
    }
  }

  updateGroupTextInSubQuestion(){
    if(this.interaction?.sub_questions && this.interaction?.sub_questions?.length){
      _.each(this.interaction?.sub_questions,(subQuestion:any)=>{
        subQuestion["group_text"]=this.interaction.text + " (" + this.interaction.field_id + ")"
      })
    }
  }
  // Edit draft question
  editDraft(id: number): void {
    // console.log('Editing draft question with ID:', id);
    const draft = this.draftQuestions.find(q => q.id === id);
    if (draft) {
      // Populate form with draft data
      this.questionTitle = draft.text;
      this.questionType = draft.type;

      // Switch to create tab
      this.setActiveTab('create');
    }
  }

  // Page management methods

  // Search pages
  searchPages(): void {
    if (!this.pageSearchQuery.trim()) {
      this.filteredPages = [...this.surveyPages];
      return;
    }

    const query = this.pageSearchQuery.toLowerCase().trim();
    this.filteredPages = this.surveyPages.filter(page =>
      page.name.toLowerCase().includes(query) ||
      page.type.toLowerCase().includes(query)
    );
  }

  // Clear page search
  clearPageSearch(): void {
    this.pageSearchQuery = '';
    this.filteredPages = [...this.surveyPages];
  }

  // Select a page for editing
  selectPage(index: number): void {
    this.selectedPageIndex = index;
    this.selectedPage = { ...this.filteredPages[index] }; // Make a copy to edit
  }

  // Add a new page
  addNewPage(): void {
    const newId = Math.max(0, ...this.surveyPages.map(p => p.id)) + 1;
    const newPage: SurveyPage = {
      id: newId,
      name: `Page-${newId}`,
      type: 'default',
      questions: [],

      // Default values for advanced options
      includeInProgress: true,
      showProgressBar: true,
      saveOnNext: true,
      enableAutoScroll: true,
      showNextButton: true,
      nextButtonText: 'Next',
      backButtonText: 'Back',
      isLinkExpiredPage: false,
      isResponseSubmittedPage: false,
      isThankYouPage: false,
      enablePageShuffling: true
    };

    this.surveyPages.push(newPage);
    this.filteredPages = [...this.surveyPages];

    // Select the new page for editing
    this.selectPage(this.filteredPages.length - 1);
  }

  // Edit page (prevent event bubbling)
  editPage(index: number, event: MouseEvent): void {
    event.stopPropagation(); // Prevent the row click event
    this.selectPage(index);

    // Open the edit modal
    this.openPageEditModal();
  }

  // Open page edit modal
  openPageEditModal(): void {
    this.showPageEditModal = true;
    this.activePageEditTab = 'basic';

    // Initialize question lists
    if (this.selectedPage && this.selectedPage.questions) {
      this.currentPageQuestions = this.selectedPage.questions
        .map(id => this.questionsList.find(q => q.id === id))
        .filter(q => q !== undefined) as QuestionItem[];

      this.availableQuestions = this.questionsList.filter(q =>
        !this.selectedPage?.questions?.includes(q.id)
      );
    } else {
      this.currentPageQuestions = [];
      this.availableQuestions = [...this.questionsList];
    }

    // For new simplified UI
    this.allQuestions = [...this.questionsList];
    this.filteredAllQuestions = [...this.allQuestions];
    this.questionSearchQuery = '';
  }

  // Close page edit modal
  closePageEditModal(): void {
    this.showPageEditModal = false;
  }

  // Set active tab in page edit modal
  setPageEditTab(tab: string): void {
    this.activePageEditTab = tab;
  }

  // Move a question to the page
  moveQuestionToPage(question?: QuestionItem): void {
    if (!this.selectedPage) {
      return;
    }

    const questionToMove = question || (this.availableQuestions.length > 0 ? this.availableQuestions[0] : null);

    if (!questionToMove) {
      return; // No question to move
    }

    // Move from available to current
    this.availableQuestions = this.availableQuestions.filter(q => q.id !== questionToMove.id);
    this.currentPageQuestions.push(questionToMove);

    // Update the selected page question list
    if (!this.selectedPage.questions) {
      this.selectedPage.questions = [];
    }
    this.selectedPage.questions.push(questionToMove.id);
  }

  // Remove a question from the page
  removeQuestionFromPage(question?: QuestionItem): void {
    if (!this.selectedPage) {
      return;
    }

    const questionToRemove = question || (this.currentPageQuestions.length > 0 ? this.currentPageQuestions[0] : null);

    if (!questionToRemove) {
      return; // No question to remove
    }

    // Move from current to available
    this.currentPageQuestions = this.currentPageQuestions.filter(q => q.id !== questionToRemove.id);
    this.availableQuestions.push(questionToRemove);

    // Update the selected page question list
    if (this.selectedPage.questions) {
      this.selectedPage.questions = this.selectedPage.questions.filter(id => id !== questionToRemove.id);
    }
  }

  // Save changes from the page edit modal
  savePageEdit(): void {
    if (!this.selectedPage || this.selectedPageIndex === null) {
      this.closePageEditModal();
      return;
    }

    const pageId = this.selectedPage.id;
    // Find the index in the original array
    const realIndex = this.surveyPages.findIndex(p => p.id === pageId);

    if (realIndex >= 0) {
      // Update the page in the original array
      this.surveyPages[realIndex] = { ...this.selectedPage };

      // Update the filtered array if it exists in there
      const filteredIndex = this.filteredPages.findIndex(p => p.id === pageId);
      if (filteredIndex >= 0) {
        this.filteredPages[filteredIndex] = { ...this.selectedPage };
      }
    }

    this.showPageEditModal = false;
  }

  // Delete page
  deletePage(index: number, event: MouseEvent): void {
    event.stopPropagation(); // Prevent the row click event

    const pageId = this.filteredPages[index].id;
    // Find the real index in the original array
    const realIndex = this.surveyPages.findIndex(p => p.id === pageId);

    if (realIndex >= 0) {
      // Remove the page
      this.surveyPages.splice(realIndex, 1);
      this.filteredPages = this.filteredPages.filter(p => p.id !== pageId);

      // Clear selection if the deleted page was selected
      if (this.selectedPageIndex === index) {
        this.selectedPageIndex = null;
        this.selectedPage = null;
      }
    }
  }

  // Cancel page editing
  cancelPageEdit(): void {
    this.selectedPageIndex = null;
    this.selectedPage = null;
  }

  // Save page changes
  savePageChanges(): void {
    if (this.selectedPage && this.selectedPageIndex !== null) {
      const pageId = this.selectedPage.id;
      const realIndex = this.surveyPages.findIndex(p => p.id === pageId);

      if (realIndex >= 0) {
        // Update the page in the original array
        this.surveyPages[realIndex] = { ...this.selectedPage };

        // Update the filtered array
        this.filteredPages[this.selectedPageIndex] = { ...this.selectedPage };
      }
    }
  }

  // Drag and drop methods

  // Start drag on mouse down
  startDrag(index: number, event: MouseEvent): void {
    // Prevent default to avoid text selection
    event.preventDefault();

    // Simplified approach that avoids creating synthetic DragEvent objects
    const listItem = (event.target as HTMLElement).closest('.page-list-item');
    if (listItem) {
      listItem.setAttribute('draggable', 'true');
      // The native dragstart event will be triggered naturally
    }
  }

  // Handle dragstart event
  onDragStart(index: number, event: DragEvent): void {
    this.draggedPageIndex = index;

    // Set data for drag operation
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index.toString());

      // Simplified approach that doesn't try to create custom ghost elements
      const target = event.target as HTMLElement;
      target.classList.add('dragging');
    }
  }

  // Handle dragover event
  onDragOver(event: DragEvent): void {
    // Prevent default to allow drop
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  // Handle dragenter event
  onDragEnter(index: number, event: DragEvent): void {
    // Prevent default
    event.preventDefault();

    // Update target index if different from dragged item
    if (index !== this.draggedPageIndex) {
      this.dragTargetIndex = index;
    } else {
      this.dragTargetIndex = null;
    }
  }

  // Handle dragleave event
  onDragLeave(event: DragEvent): void {
    // Only clear if leaving outside the list
    const target = event.target as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;

    if (relatedTarget && !target.contains(relatedTarget) && !target.closest('.pages-list')?.contains(relatedTarget)) {
      this.dragTargetIndex = null;
    }
  }

  // Handle drop event
  onDrop(index: number, event: DragEvent): void {
    // Prevent default browser behavior
    event.preventDefault();

    // Get the dragged item index
    if (this.draggedPageIndex !== null && this.draggedPageIndex !== index) {
      // Get the current item
      const draggedItem = this.filteredPages[this.draggedPageIndex];

      // Remove the item from its original position
      this.filteredPages.splice(this.draggedPageIndex, 1);

      // Insert the item at the new position
      this.filteredPages.splice(index, 0, draggedItem);

      // Update the original array as well
      this.surveyPages = [...this.filteredPages];

      // If the selected page was dragged, update its index
      if (this.selectedPageIndex === this.draggedPageIndex) {
        this.selectedPageIndex = index;
      }
      // If the selected page was moved due to the drag, update its index
      else if (this.selectedPageIndex !== null) {
        if (this.draggedPageIndex < this.selectedPageIndex && index >= this.selectedPageIndex) {
          this.selectedPageIndex--;
        } else if (this.draggedPageIndex > this.selectedPageIndex && index <= this.selectedPageIndex) {
          this.selectedPageIndex++;
        }
      }
    }

    // Reset drag state
    this.draggedPageIndex = null;
    this.dragTargetIndex = null;
  }

  // Handle dragend event
  onDragEnd(): void {
    // Reset drag state
    this.draggedPageIndex = null;
    this.dragTargetIndex = null;
  }

  // Handle Link Expired page toggle
  toggleLinkExpiredPage(isEnabled: boolean): void {
    if (!this.selectedPage) return;

    if (isEnabled) {
      // Set this page as a Link Expired page
      this.selectedPage.type = 'expired';
      this.selectedPage.isLinkExpiredPage = true;
      this.selectedPage.isResponseSubmittedPage = false;
      this.selectedPage.isThankYouPage = false;
      this.selectedPage.includeInProgress = false;
      this.selectedPage.showProgressBar = false;
      this.selectedPage.saveOnNext = false;
      this.selectedPage.showNextButton = false;
      this.selectedPage.enablePageShuffling = false;
    } else {
      // Reset to default page
      this.selectedPage.type = 'default';
      this.selectedPage.isLinkExpiredPage = false;
      this.selectedPage.enablePageShuffling = true;
    }
  }

  // Handle Response Already Submitted page toggle
  toggleResponseSubmittedPage(isEnabled: boolean): void {
    if (!this.selectedPage) return;

    if (isEnabled) {
      // Set this page as a Response Already Submitted page
      this.selectedPage.type = 'submitted';
      this.selectedPage.isResponseSubmittedPage = true;
      this.selectedPage.isLinkExpiredPage = false;
      this.selectedPage.isThankYouPage = false;
      this.selectedPage.includeInProgress = false;
      this.selectedPage.showProgressBar = false;
      this.selectedPage.saveOnNext = false;
      this.selectedPage.showNextButton = false;
      this.selectedPage.enablePageShuffling = false;
    } else {
      // Reset to default page
      this.selectedPage.type = 'default';
      this.selectedPage.isResponseSubmittedPage = false;
      this.selectedPage.enablePageShuffling = true;
    }
  }

  // Handle Link Expired checkbox change event
  onLinkExpiredChange(event: Event): void {
    const isChecked = (event.target as HTMLInputElement)?.checked || false;
    this.toggleLinkExpiredPage(isChecked);
  }

  // Handle Response Submitted checkbox change event
  onResponseSubmittedChange(event: Event): void {
    const isChecked = (event.target as HTMLInputElement)?.checked || false;
    this.toggleResponseSubmittedPage(isChecked);
  }

  // Handle Thank You page toggle
  onThankYouPageChange(event: Event): void {
    const isChecked = (event.target as HTMLInputElement)?.checked || false;
    this.toggleThankYouPage(isChecked);
  }

  // Toggle Thank You page
  toggleThankYouPage(isEnabled: boolean): void {
    if (!this.selectedPage) return;

    if (isEnabled) {
      // Set this page as a Thank You page
      this.selectedPage.type = 'thank-you';
      this.selectedPage.isThankYouPage = true;
      this.selectedPage.isLinkExpiredPage = false;
      this.selectedPage.isResponseSubmittedPage = false;
      this.selectedPage.includeInProgress = false;
      this.selectedPage.showProgressBar = false;
      this.selectedPage.showNextButton = false;
    } else {
      // Reset to default page
      this.selectedPage.type = 'default';
      this.selectedPage.isThankYouPage = false;
    }
  }

  /**
   * Filter questions based on search query
   */
  filterQuestions(): void {
    if (!this.questionSearchQuery) {
      this.filteredAllQuestions = [...this.allQuestions];
      return;
    }

    const searchTerm = this.questionSearchQuery.toLowerCase();
    this.filteredAllQuestions = this.allQuestions.filter(question =>
      question.text.toLowerCase().includes(searchTerm) ||
      question.type.toLowerCase().includes(searchTerm)
    );
  }

  /**
   * Check if a question is selected for the current page
   */
  isQuestionSelected(question: QuestionItem): boolean {
    return this.currentPageQuestions.some(q => q.id === question.id);
  }

  /**
   * Toggle question selection
   */
  toggleQuestionSelection(question: QuestionItem): void {
    if (this.isQuestionSelected(question)) {
      this.removeQuestionFromPage(question);
    } else {
      this.addQuestionToPage(question);
    }
  }

  /**
   * Add a question to the current page
   */
  addQuestionToPage(question: QuestionItem): void {
    if (!this.selectedPage) return;

    if (!this.currentPageQuestions.some(q => q.id === question.id)) {
      this.currentPageQuestions.push(question);

      // Update the page's questions array
      if (!this.selectedPage.questions) {
        this.selectedPage.questions = [];
      }
      this.selectedPage.questions.push(question.id);
    }
  }

  /**
   * Handle dropping a question when reordering
   */
  dropQuestion(event: CdkDragDrop<QuestionItem[]>): void {
    if (!this.selectedPage || !this.selectedPage.questions) return;

    // Move the item in the array
    moveItemInArray(this.currentPageQuestions, event.previousIndex, event.currentIndex);

    // Update the page's questions array to match the new order
    this.selectedPage.questions = this.currentPageQuestions.map(q => q.id);
  }

  /**
   * Check if all questions are selected
   */
  areAllQuestionsSelected(): boolean {
    if (!this.filteredAllQuestions.length || !this.currentPageQuestions.length) {
      return false;
    }

    return this.filteredAllQuestions.every(q => this.isQuestionSelected(q));
  }

  /**
   * Toggle selection of all questions
   */
  toggleAllQuestions(): void {
    if (this.areAllQuestionsSelected()) {
      // Deselect all questions
      this.filteredAllQuestions.forEach(question => {
        if (this.isQuestionSelected(question)) {
          this.removeQuestionFromPage(question);
        }
      });
    } else {
      // Select all questions
      this.filteredAllQuestions.forEach(question => {
        if (!this.isQuestionSelected(question)) {
          this.addQuestionToPage(question);
        }
      });
    }
  }

  // Drag and drop for regular questions
  onQuestionDragStart(event: DragEvent, index: number) {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', index.toString());
      const element = event.target as HTMLElement;
      element.classList.add('dragging');
    }
  }

  onQuestionDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onQuestionDragEnter(event: DragEvent, index: number) {
    event.preventDefault();
    const element = event.target as HTMLElement;
    const listItem = element.closest('.question-list-item');
    if (listItem) {
      listItem.classList.add('drag-over');
    }
  }

  onQuestionDragLeave(event: DragEvent) {
    const element = event.target as HTMLElement;
    const listItem = element.closest('.question-list-item');
    if (listItem) {
      listItem.classList.remove('drag-over');
    }
  }

  onQuestionDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    const draggedIndex = parseInt(event.dataTransfer?.getData('text/plain') || '-1');
    if (draggedIndex > -1 && draggedIndex !== dropIndex) {
      const questions = [...this.filteredQuestions];
      const [removed] = questions.splice(draggedIndex, 1);
      questions.splice(dropIndex, 0, removed);
      this.filteredQuestions = questions;
    }
    this.clearDragStyles();
  }

  onQuestionDragEnd() {
    this.clearDragStyles();
  }

  // Drag and drop for draft questions
  onDraftQuestionDragStart(event: DragEvent, index: number) {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', index.toString());
      const element = event.target as HTMLElement;
      element.classList.add('dragging');
    }
  }

  onDraftQuestionDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDraftQuestionDragEnter(event: DragEvent, index: number) {
    event.preventDefault();
    const element = event.target as HTMLElement;
    const listItem = element.closest('.question-list-item');
    if (listItem) {
      listItem.classList.add('drag-over');
    }
  }

  onDraftQuestionDragLeave(event: DragEvent) {
    const element = event.target as HTMLElement;
    const listItem = element.closest('.question-list-item');
    if (listItem) {
      listItem.classList.remove('drag-over');
    }
  }

  onDraftQuestionDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    const draggedIndex = parseInt(event.dataTransfer?.getData('text/plain') || '-1');
    if (draggedIndex > -1 && draggedIndex !== dropIndex) {
      const questions = [...this.draftQuestions];
      const [removed] = questions.splice(draggedIndex, 1);
      questions.splice(dropIndex, 0, removed);
      this.draftQuestions = questions;
    }
    this.clearDragStyles();
  }

  onDraftQuestionDragEnd() {
    this.clearDragStyles();
  }

  private clearDragStyles() {
    document.querySelectorAll('.question-list-item').forEach(item => {
      item.classList.remove('dragging', 'drag-over');
    });
  }

  // Toggle accordion expanded state
  toggleQuestionExpanded(question: QuestionItem, event: MouseEvent): void {
    // Stop propagation to prevent interference with other click handlers
    event.stopPropagation();

    // Toggle the expanded state
    question.expanded = !question.expanded;
  }



  private parseLogicString(logicString: string, logicAction: string) {
    // Split the logic string into individual conditions
    const conditions = logicString.split(/(\|\||&&)/);
    const transformString: TransformCondition[] = [];

    for (let i = 0; i < conditions.length; i += 2) {
      if (conditions[i].trim()) {
        const condition = this.parseCondition(conditions[i].trim());
        if (condition) {
          transformString.push({
            ...condition,
            logicalOptr: i > 0 ? conditions[i - 1] : '||'
          });
        }
      }
    }

    this.componentConditions.logicDetail[logicAction].transformString = transformString;
    this.componentConditions.logicDetail[logicAction].transformOrignal = [...transformString];
  }

  private parseCondition(conditionString: string): TransformCondition | null {
    // Parse a single condition string into its components
    const match = conditionString.match(/([^=!<>]+)([=!<>]+)(.+)/);
    if (match) {
      const [, question, operator, value] = match;
      return {
        interaction: question.trim(),
        comparisonOptr: operator.trim(),
        value: value.trim(),
        target: 'target_value',
        configuration: [],
        logicalOptr: '||',
        isOpen: false
      };
    }
    return null;
  }

  addVisibilityCondition() {
    if (this.validateCondition(this.visibilityCondition)) {
      const condition = { ...this.visibilityCondition };
      this.visibilityConditions.push(condition);
      this.updateVisibilityConditions();
      this.resetCondition(this.visibilityCondition);
    }
  }

  addMandatoryCondition() {
    if (this.validateCondition(this.mandatoryCondition)) {
      const condition = { ...this.mandatoryCondition };
      this.mandatoryConditions.push(condition);
      this.updateMandatoryConditions();
      this.resetCondition(this.mandatoryCondition);
    }
  }

  private updateVisibilityConditions() {
    const logicString = this.buildLogicString(this.visibilityConditions);
    this.interaction['visible-if'] = logicString;
    this.componentConditions.logicDetail['1'].logicString = logicString;
  }

  private updateMandatoryConditions() {
    const logicString = this.buildLogicString(this.mandatoryConditions);
    this.interaction['is_mandatory_if'] = logicString;
    this.componentConditions.logicDetail['2'].logicString = logicString;
  }

  private buildLogicString(conditions: Condition[]): string {
    if (conditions.length === 0) return '';

    return conditions.map((condition, index) => {
      const operator = this.getOperatorSymbol(condition.operator);
      const questionText = this.getQuestionText(condition.question);
      const valueText = this.getOptionText(condition.value);
      return `${questionText}${operator}${valueText}`;
    }).join('||');
  }

  private getOperatorSymbol(operator: string): string {
    const operatorMap = {
      'equals': '==',
      'not_equals': '!=',
      'contains': 'contains',
      'greater_than': '>',
      'less_than': '<'
    };
    return operatorMap[operator] || operator;
  }

  getQuestionText(fieldId: string): string {
    const question = this.questionsList.find(q => q.field_id === fieldId);
    return question ? question.text : '';
  }

  getOptionText(value: string): string {
    // This should be implemented based on your data structure
    // For now returning the value as is
    return value;
  }

  getQuestionOptions(fieldId: string): any[] {
    // This should be implemented based on your data structure
    // For now returning an empty array
    return [];
  }

  private validateCondition(condition: Condition): boolean {
    if (!condition.question || !condition.operator || !condition.value) {
      // TODO: Replace with proper error handling
      console.error('Please fill in all condition fields');
      return false;
    }
    return true;
  }

  private resetCondition(condition: Condition) {
    condition.question = '';
    condition.operator = '';
    condition.value = '';
  }

  removeVisibilityCondition(index: number) {
    this.visibilityConditions.splice(index, 1);
    this.updateVisibilityConditions();
  }

  removeMandatoryCondition(index: number) {
    this.mandatoryConditions.splice(index, 1);
    this.updateMandatoryConditions();
  }


  invokeShuffling() {
    var allowMultiSelect = this.interaction.allowMultiSelect;
    if (allowMultiSelect === false) {
      this.interaction.enable_shuffle = false;
      this.interaction.enableConditionalLabels = false;
      var subQuestions = this.interaction.sub_questions || [];
      subQuestions.forEach(function (question) {
        question.enable_shuffle = false;
      })
    }
  }

  /**
     * @description : this is the when user creating the new interaction and created options 
     * but after creating the option the user want to enable the parent child feature.
     * @param  {boolean} isChecked
     */
  handleMultipleParentChild(isChecked) {
    if (isChecked && this.interaction.sub_questions && this.interaction.sub_questions.length) {
      var sub_ques = this.interaction.sub_questions;
      this.multiSelectModel[this.interaction.field_id] = {};
      for (var key in sub_ques) {
        this.multiSelectModel[this.interaction.field_id][sub_ques[key].field_id] = [];
      }
    }
  }

  enableConditionalLabelOption() {
    if (this.interaction.enableConditionalLabels) {
      Helpers.smallAlertBox("Success", "Conditional Labels tab has been enabled to configure Conditional Labels");
    }
  }

  getComponentImage(component_name?: string) {
    let deviceType: any = this.currentDevice
    if (this.currentDevice == 'tablet') {
      deviceType = 'desktop'
    }
    if (component_name) {
      return `https://static1.litmusworld.com/component_screenshot/${deviceType}/${this.selectedComponent.name}.png`
    } else {
      return `https://static1.litmusworld.com/component_screenshot/${deviceType}/no-preview.jpg`
    }
  }

  accordians: any = {
    parentChildLinkage: false,
    conditionalLabels: false,
    setupOptions: false
  }
  toggleAccordianPanel(type: any) {
    this.accordians[type] = !this.accordians[type];
  }

  // This function is used to update the selected interaction if conditional labek is changed.
  updateQuestion(event: any) {
    this.interaction = event
    this.markDirty();
  }
  parentLinkingChanged(event: any) {
    this.multiSelectModel = event.multiSelectModel;
    this.interaction = event.interaction
    this.markDirty();
  }




  // Condition Panel Logics **** Start ****

  /**
   * @name  currentConditions
   * @description  handeling updating of interaction condition
   * @type {Object}:  logicDetail{Object} , logicAction{number} 
   **/
  componentConditionsSchema: any = {
    logicDetail: {
      "1": {
        "logicString": null,
        "logicKey": "1",
        "editMode": false,
        "transformString": [],
        "transformOrignal": []
      },
      "2": {
        "logicString": null,
        "logicKey": "2",
        "editMode": false,
        "transformString": [],
        "transformOrignal": []
      },
      "3": {
        "logicString": null,
        "logicKey": "3",
        "editMode": false,
        "transformString": null,
        "transformOrignal": null
      }
    },
    logicAction: "1"
  }
  componentConditions: any = Helpers.deepClone(this.componentConditionsSchema)
  /**
   * @name operators
   * @description  for storing  operators 
   * @type {Object}  logical{Object} , comparison{Object}
   * @note please make sure comparison object must be descending sorted order as comparison.text length.  
   **/
  operators: any = {
    logical: [{
      op: "||",
      text: "OR"
    }, {
      op: "&&",
      text: "AND"
    }],
    comparison: [{
      op: ">=",
      text: "Greater than Equals"
    }, {
      op: "<=",
      text: "Less than Equals"
    }, {
      op: ">",
      text: "Greater than"
    }, {
      op: "<",
      text: "Less than"
    }, {
      op: "!=",
      text: "Not Equals"
    }, {
      op: "==",
      text: "Equals"
    }]
  };
  operatorsMap: any = {
    '||': {
      op: "||",
      text: "OR"
    },
    '&&': {
      op: "&&",
      text: "AND"
    },
    '>=': {
      op: ">=",
      text: "Greater than Equals"
    },
    '<=': {
      op: "<=",
      text: "Less than Equals"
    },
    '>': {
      op: ">",
      text: "Greater than"
    },
    '<': {
      op: "<",
      text: "Less than"
    },
    '!=': {
      op: "!=",
      text: "Not Equals"
    },
    '==': {
      op: "==",
      text: "Equals"
    }
  }
  // Add these properties to the component class


  actions = {
    '1': {
      value: '1',
      text: 'Show',
      actionProperty: 'visible-if'
    },
    '2': {
      value: '2',
      text: 'Mandatory',
      actionProperty: 'is_mandatory_if'
    },
    '3': {
      value: '3',
      text: 'Server Visible',
      actionProperty: 'server-visible-if'
    }
  };

  questions: any = []
  questionsAndGroups: any = []
  questionsAndGroupsForOptions: any = []

  // Conditions panel state
  isConditionsPanelOpen = false;
  activeConditionTab: string = 'visibility';

  // Condition models
  visibilityCondition: Condition = {
    question: '',
    operator: '',
    value: ''
  };
  mandatoryCondition: Condition = {
    question: '',
    operator: '',
    value: ''
  };

  // Condition lists
  visibilityConditions: Condition[] = [];
  mandatoryConditions: Condition[] = [];

  toggleConditionsPanel() {
    this.isConditionsPanelOpen = !this.isConditionsPanelOpen;
    this.componentConditions = Helpers.deepClone(this.componentConditionsSchema);
    this.initializeConditions(); 
  }

  setConditionTab(tab: any) {
    this.activeConditionTab = tab;
    this.componentConditions = Helpers.deepClone(this.componentConditionsSchema);
    this.componentConditions.logicAction = '3'
    if (tab === 'visibility') {
      this.componentConditions.logicAction = '1'
    } else if (tab === 'mandatory') {
      this.componentConditions.logicAction = '2'
    }

    this.initializeConditions();
  }

  private initializeConditions() {

    const logicAction = this.componentConditions.logicAction;
    const actionProperty = this.actions[logicAction].actionProperty;
    this.editCondition(logicAction);
    this.newExpression('1');
    this.newExpression('2');
    this.newExpression('3');

    if (this.isEditMode) {
      this.questionsAndGroups = this.getFilteredQuestions(this.interaction, null, logicAction)
    } else {
      this.questionsAndGroups = this.getFilteredQuestions(null, null, logicAction)
    }

  }
  isLogicAvailable: boolean = false;

  /*
    * @name  editCondition
    * @description edit the condition of interaction for show/Mandatory/Server Visible 
    * and convert condition string in to [getQueryStringObject]
    * @param {string} logicAction - logic action['1'|'2'] for show or mandatory condition
    *
    */
  editCondition(logicAction, noNewLogic?: any) {
    if (logicAction) {
      this.isEditMode = true;
    }


    // this.setQuestions();
    this.setQuestionConditions();
    // // resetComponentConditions();
    this.copyQuestionConditions();

    this.componentConditions.logicAction = logicAction ? logicAction : '1';
    // looop through each actions {Show, Mandatory, Server Visible}
    _.each(this.componentConditions.logicDetail, (logic, index) => {
      //true for show and mandatory actions and is edit mode true
      //if we have server visible i.e logicKey is 3 then provide logic string as it is 
      if (logic.logicKey != 3) {
        if (_.get(logic, 'transformString') && _.get(logic, 'logicString') && (typeof logic.logicString == 'string')) {
          if (logic.transformString.length > 0) {
            logic.transformString = [];
          }
          //split string on OR | AND as a seperator 
          var expressions = logic.logicString.split(/\&\&|\|\|/);
          //remove logic expresstion from string for getting AND & OR operator array 
          var conditions = _.compact(logic.logicString.match(/(&&)?(\|\|)*/g));
          //loop through expressions getting from logic string
          _.each(expressions, (expression, index) => {
            //assign logical operator if not a first operator
            var logicalOperator = index > 0 ? conditions[index - 1] : '||';
            //create object from sring
            var stringObject = this.createObjectFromExpression(expression, logicalOperator);
            stringObject.logicString = this.getFormatedLogicString(stringObject);
            if (stringObject.configuration && stringObject.value) {
              const config = _.find(stringObject.configuration, function (config) {
                return stringObject.value == config.value;
              });
              stringObject.value = config?.value || '';
            }
            stringObject.isOpen = false;
            var isValidity = this.formValidity(stringObject);
            stringObject.isValid = (isValidity) ? true : false;
            //push above created query string object in transform string array of respective action 
            logic.transformString.push(stringObject);
            //if edit mode is true then set it to false
            if (!logic.editMode) {
              //copying string transform object to array variable
              logic.transformOrignal.push(Helpers.deepClone(stringObject));
            }
          });
          //set sub action editmode to true
          logic.editMode = true;
        }
        //  else if (!noNewLogic) {
        //     //logic string is empty then initialize query string object
        //     logic.transformString = [getQueryStringObject({})];
        // }
      }
    });
    this.isLogicAvailable = true;
  };

  getFormatedLogicString(queryObject) {
    var logicStr = '';
    var targetInteractionQuestion = '';
    var interactionId = [];

    if (queryObject.value && isNaN(queryObject.value) && queryObject.value.indexOf("rating.") > -1) {
      if (queryObject.value.indexOf("primary_rating") > -1) {
        queryObject.targetInteraction = 'primary_rating';
      } else {
        interactionId = queryObject.value.split("['");
        if (interactionId[interactionId.length - 1]) {
          const lastInteractionId: string = interactionId[interactionId.length - 1];
          queryObject.targetInteraction = lastInteractionId.replace("']", '');
        }
      }

      queryObject.target = 'target_field';
    }

    _.each(this.questions, (questions: any) => {
      if (queryObject.interaction && questions.field_id) {
        if (questions.field_id == queryObject.targetInteraction && queryObject.target == 'target_field') {
          targetInteractionQuestion = questions.question;
        }

        if (questions.field_id === queryObject.interaction) {
          logicStr = logicStr + (questions.question || '') + '  ' + (queryObject.comparisonOptr || '') + '  ';
        }
      }
    });


    if (queryObject.configuration && (!queryObject.targetInteraction || !(queryObject.target == 'target_field'))) {
      for (var j = 0; j < queryObject.configuration.length; j++) {
        if (queryObject.configuration[j].value && queryObject.value) {
          if (queryObject.configuration[j].value == queryObject.value) {
            logicStr = logicStr + '"' + (queryObject.configuration[j].label || '') + '"';
            break;
          }
        }
      }
    } else {
      logicStr = logicStr + ' "' + targetInteractionQuestion + '"';
    }

    return logicStr;
  }

  /*
           * @name  createObjectFromExpression
           * for e.g. condition string:
           *      (detailed_rating['question_9436981327']<=2)
           *  condition object string :
           *  
           *  queryStringObject: {
           *   interaction: 'question_9436981327';
           *   comparisonOptr: '<=';
           *   target: 'target_Field';
           *   value: '2';
           *   targetInteraction: null
           *   logicalOptr: 'OR';
           *   configuration: [{value: 1, text'Very Poor'},
           *                  {value: 2, text'Poor'},
           *                  {value: 3, text'Good'}]}]
           * @description create getQueryStringObject object for action's logic transform string from string expression, asssigning logical operator 
           * @param {string} expression - expression string 
           * @param {string} logicalOperator - logical operator for e.g. ['&&'|'||'] 
           *  
           */
  createObjectFromExpression(expression: any, logicalOperator: any) {
    //initialize query string object with target and logical operator property
    var queryStringObject = this.getQueryStringObject({});

    //compaire operator.comparison with the string for picking up expression operator
    _.each(this.operators.comparison, (comparisonOptr: any, index: any) => {
      //true if operator found in a string
      if (expression.lastIndexOf(comparisonOptr.op) > -1) {
        //assigning question in the object 
        queryStringObject.interaction = expression.substr(0, expression.lastIndexOf(comparisonOptr.op) - 1);
        //getting question parameter of selected question and assign it to configuration
        queryStringObject.configuration = this.getQuestionParameters(queryStringObject.interaction);
        //split expresson on the basis of coparison operator
        var literals = expression.split(comparisonOptr.op);
        //assigning question in the object
        switch (literals[0]) {
          case 'rating.primary_rating':
            queryStringObject.interaction = 'primary_rating';
            break;
          case 'rating.comments':
            queryStringObject.interaction = 'comments';
            break;
          default:
            queryStringObject.interaction = literals[0].substr(24).slice(0, -2);
        }

        //getting question parameter of selected question and assign it to configuration
        queryStringObject.configuration = this.getQuestionParameters(queryStringObject.interaction as any);
        var targetString = literals[1];
        queryStringObject.target = 'target_field';

        //assigning target question/value in the object
        switch (literals[1]) {
          case 'rating.primary_rating':
            queryStringObject.targetInteraction = 'primary_rating';
            break;
          case 'rating.comments':
            queryStringObject.targetInteraction = 'comments';
            break;
          default:
            if (this.isInteraction(targetString as any)) {
              //assign to query string object
              //removing "rating.detailed_rating['']" from logic string 
              queryStringObject.targetInteraction = literals[1].substr(24).slice(0, -2);
            } else {
              queryStringObject.target = 'target_value';
              //remove blank spaces from string splited above and assign to query string object
              queryStringObject.value = targetString;
            }
        }
        //assign comparison operator
        queryStringObject.comparisonOptr = comparisonOptr.op;
        queryStringObject.logicalOptr = logicalOperator;
        //returning from loop
        return false;
      }
      return true;

    });

    return queryStringObject;
  };

  /*
        * @name  getQuestionParameters
        * @description set question parameter for question/interaction options as configuration object
        * @param {string} fieldId - field id
        * @returns {[Object]|null} - configuration parameter or null
        */
  getQuestionParameters(fieldId: any) {
    var question = this.getQuestionByFieldId(fieldId);
    if (_.get(question, 'prop')) {
      return _.map(question.prop, (prop: any) => {
        return prop;
      });
    } else {
      return null;
    }
  };


  /*
         * @name  isInteraction
         * @description check whether given string is question/interaction or not
         * @param {string} fieldId - field id
         * @return {boolean} - true is value is question/interaction else false
         *  
         */
  isInteraction(fieldId: any) {
    var question = undefined;
    if (_.get(this._conversation, 'configuration.fields')) {
      question = _.find(this._conversation.configuration.fields, {
        'field_id': fieldId
      });
    }
    return (typeof question === 'object');
  };

  questionConditions: any = {};
  /**
         * @name  setQuestionConditions
         * @description  getting all question conditions from parent in this case conversation designer 
         * @type {Object} - logicDetail{Object} , logicAction{number} 
         **/
  setQuestionConditions() {
    this.questionConditions = {};
    _.each(this.actions, (action: any) => {
      if (_.get(this.interaction, action.actionProperty)) {
        this.questionConditions[action.actionProperty] = this.interaction[action.actionProperty];
      }
    });
  };

  /*
           * @name  copyQuestionConditions
           * @description copying conditions to local instance from parent instance in this case conversation designer 
           * 
           */
  copyQuestionConditions() {
    _.each(this.componentConditions.logicDetail, (action: any) => {
      if (_.get(this.questionConditions, this.actions[action.logicKey].actionProperty)) {
        action.logicString = this.questionConditions[this.actions[action.logicKey].actionProperty];
      }
    });
  };

  /*
         * @name  newExpression
         * @description push or add new default/empty object in logic string transformation
         * @param {string} logicAction - logic action['1'|'2'] for show or mandatory condition
         *  
         */
  newExpression(logicAction: any) {
    if (this.componentConditions.logicDetail && this.componentConditions.logicDetail[logicAction] && this.componentConditions.logicDetail[logicAction].transformString) {
      _.each(this.componentConditions.logicDetail[logicAction].transformString, (expression: any) => {
        if (expression.isOpen) {
          expression.isOpen = false;
        }
      });
    }

    if (_.get(this.componentConditions, 'logicDetail.' + logicAction + '.transformString') && _.isArray(this.componentConditions.logicDetail[logicAction].transformString)) {
      this.componentConditions.logicDetail[logicAction].transformString.push(this.getQueryStringObject({}));
    }

  };




  /*
         * @name getQueryStringObject
         * @typedef {Object} getQueryStringObject
         * @property {Object.string} interaction - interaction
         * @property {Object.string} comparisonOptr - comparison operator string
         * @property {Object.string} target - target field or target value
         * @property {Object.string} value - value compairing with question input
         * @property {Object.string} logicalOptr - logical operator string
         * @property {Object[Object]} configuration - configuration for the parameter
         * @return {Object} stringObject - query string object 
         **/
  getQueryStringObject(queryStringObject) {
    var stringObject: any = {};
    stringObject.interaction = _.get(queryStringObject, 'interaction') ? queryStringObject.interaction : '';
    stringObject.comparisonOptr = _.get(queryStringObject, 'comparisonOptr') ? queryStringObject.comparisonOptr : '';
    stringObject.value = _.get(queryStringObject, 'value') ? queryStringObject.value : '';
    stringObject.targetInteraction = _.get(queryStringObject, 'targetInteraction') ? queryStringObject.targetInteraction : '';
    stringObject.logicalOptr = _.get(queryStringObject, 'logicalOptr') ? queryStringObject.logicalOptr : '||';
    stringObject.target = _.get(queryStringObject, 'target') ? queryStringObject.target : '';
    stringObject.configuration = _.get(queryStringObject, 'configuration') ? queryStringObject.configuration : [];
    stringObject.isOpen = true;
    return stringObject;
  };

  initializeQuestionsForCondition() {
    this.questions = [];
    _.each(this.conversationFields, (field: any) => {
      var interactionType = _.get(field, 'interaction_type');
      if (interactionType != 'label' && interactionType != 'image' && !field.hasOwnProperty('readOnly')) {
        if (field.hasOwnProperty('group_id') && field.hasOwnProperty('group_text') && (field.interaction_type == 'multi-select')) {
          this.questions.push(field);
        } else if (!field.hasOwnProperty('group_id') && !field.hasOwnProperty('group_text') && !_.get(field, 'allowMultiSelect')) {
          this.questions.push(field);
        }
      }
    });
  }

  getFilteredQuestions(currentQuestion: any, targetField: any, action?: any): (Question | QuestionGroup)[] {
    const currentQuestionId = _.get(currentQuestion, 'field_id');
    let questions = _.filter(_.cloneDeep(this.questions), (question: any) => {
      const fieldId = _.get(question, 'field_id');
      const groupId = _.get(question, 'group_id');
      if ((currentQuestionId && fieldId && ((currentQuestionId == fieldId)) || (groupId && (groupId == currentQuestionId)))) {
        if (action == '2' && (groupId == currentQuestionId)) {
          return true;
        } else {
          return false;
        }
      }
      if (targetField && targetField == _.get(question, 'field_id')) {
        return false;
      }
      return true;
    })
    let groups: any = {}
    _.each(questions, (question: any) => {
      if (question.group_text) {
        if (!groups[question.group_id!]) {
          groups[question.group_id!] = {
            text: question.group_text,
            field_id: question.group_id!,
            options: []
          }
        }
        if(groups[question.group_id] && !groups[question.group_id]?.options ){
          groups[question.group_id].options = [];
        }
        groups[question.group_id].field_id = question.group_id;
        groups[question.group_id].text = question.group_text;
        groups[question.group_id].options.push(question)  
      } else {
        groups[question.field_id] = question
      }
    });
    return Object.values(groups)
  }

  isQuestionGroup(obj: any) {
    return 'options' in obj && Array.isArray(obj.options);
  }

  isQuestion(obj: any) {
    return !('options' in obj) || !Array.isArray(obj.options);
  }

  getInteractionText(fieldId: string): string {
    const question = this.questions.find((q: any) => q.field_id === fieldId);
    return question ? question.text : '';
  }


  /*
  * @name  updateTransitionObject
  * @description to set transition object after selecting/changing interaction
  * @param {integer} arrayIndex - index of an array
  * @param {Object} queryStringObject - queryStringObject
  * @param {string} logicAction - logic action 
  *
  */
  updateTransitionObject(arrayIndex, queryStringObject, logicAction) {
    var questionFields = null;
    if (queryStringObject.hasOwnProperty('interaction')) {
      questionFields = this.getQuestionByFieldId(queryStringObject.interaction);
      if (_.get(questionFields, 'prop')) {
        queryStringObject.configuration = _.map(this.questionFields.prop, function (prop) {
          return prop;
        });
      }
    }

    if (_.get(this.componentConditions, 'logicDetail.' + logicAction + '.transformString')) {
      var transitionObject = null;
      _.each(this.componentConditions.logicDetail[logicAction].transformOrignal, function (logic) {
        if (logic.interaction == queryStringObject.interaction) {
          transitionObject = Helpers.deepClone(logic);
          return;
        }
      });
      if (!transitionObject) {
        if (this.questionFields.hasOwnProperty('group_id') && this.questionFields.hasOwnProperty('group_text') && (_.get(questionFields, 'interaction_type') == 'multi-select')) {
          var value = null;
          if (queryStringObject.configuration && queryStringObject.configuration.length > 0) {
            value = queryStringObject.configuration[0].value.toString();
          }
          transitionObject = this.getQueryStringObject({
            interaction: queryStringObject.interaction,
            comparisonOptr: '==',
            target: 'target_value',
            value: value,
            logicalOptr: queryStringObject.logicalOptr
          });
        } else {
          var target = '';
          if (queryStringObject.configuration && queryStringObject.configuration.length > 0) {
            target = 'target_value';
          }
          transitionObject = this.getQueryStringObject({
            interaction: queryStringObject.interaction,
            configuration: queryStringObject.configuration,
            logicalOptr: queryStringObject.logicalOptr,
            target: target
          });
        }
      }

      if (this.componentConditions.logicDetail[logicAction].transformString.length > 0) {
        // if((componentConditions.logicDetail[logicAction].transformString.length - 1) === arrayIndex) {
        //     delete transitionObject.value;
        // }
        // componentConditions.logicDetail[logicAction].transformString[arrayIndex] = transitionObject;
      } else {
        this.componentConditions.logicDetail[logicAction].transformString.push(transitionObject);
      }

    } else {
      //console.log('no transition object found or logic action is server visible');
    }
    this.questionsAndGroupsForOptions = this.getFilteredQuestions(null, queryStringObject.interaction, logicAction)
  };


  addCondition(logic: any) {

    if (this.formValidity(logic)) {
      logic.isValid = true;
      logic.isOpen = false;
      // updateTransitionObject($index, logic, condition.componentConditions.logicAction);
      if (logic.isOpen === false && !logic.isEditMode) {
        this.newExpression(this.componentConditions.logicAction);
      }
      this.updateCurrentCondition(this.componentConditions.logicAction);
      // this.scrollDown('interactionsConditions');
    }
  }

  formValidity = function (logic) {
    logic.target = logic.target || 'target_value';
    if (logic.interaction && logic.comparisonOptr && logic.target) {
      if (logic.target == 'target_value' || logic.target == 'target_field') {
        if (logic.value || logic.targetInteraction) {
          return true;
        }
      }
    }
    return false;
  }


  // Update the relevant condition property when the user saves/edits a custom expression
  public updateCurrentCondition(logicAction: any) {
    //data memeber for copying object string to conversation designers -> question -> logic details -> logic string 
    var logicString = '';
    //data memeber for copying object string to conversation designers -> question -> (equivalent action) 
    var logicObjectString = '';
    //if action should not be server visible i.e. 3
    if (logicAction.toString() != '3' && _.get(this.componentConditions, 'logicDetail.' + logicAction + '.transformString')) {
      var firstCondition = true;
      _.each(this.componentConditions.logicDetail[logicAction].transformString, (expression) => {

        if (this.isTransformStringObjectValid(expression)) {
          if (firstCondition) {
            firstCondition = false;
          } else {
            if (_.get(this.operatorsMap, expression.logicalOptr + '.text')) {
              logicObjectString += expression.logicalOptr;
              logicString += this.operatorsMap[expression.logicalOptr].text;
            }
          }

          var srcField = this.getQuestionByFieldId(expression.interaction);
          if (_.get(expression, 'comparisonOptr')) {
            logicObjectString += this.getInteractionFieldString(srcField) + expression.comparisonOptr;
          }

          logicString += '(' + srcField.text + ' ' + this.operatorsMap[expression.comparisonOptr].text + ' ';

          if (expression.target == 'target_field') {
            var destField = this.getQuestionByFieldId(expression.targetInteraction)
            logicObjectString += this.getInteractionFieldString(destField);
            logicString += destField.text + ')';
          } else {
            if (expression.value !== null && expression.value !== undefined) {
              logicObjectString += expression.value;
              logicString += expression.value + ')';
            } else {
              logicObjectString += expression.value.value;
              logicString += expression.value.value + ')';
            }
          }
        }
      });

    } else {
      if (_.get(this.componentConditions, 'logicDetail.' + logicAction + '.logicString')) {
        const logicDetail = this.componentConditions?.logicDetail[logicAction];
        if (logicDetail?.logicString) {
          logicString = logicDetail.logicString.trim();
          logicObjectString = logicDetail.logicString.trim();
        }
      }
    }

    /** Check is parent iteraction as well as logic string is there or not */
    if (this.componentConditions.logicDetail[logicAction].logicString) {
      var parentQueExpressions: any = [];
      if (logicAction == 3) {
        var tempCurrentParentLogical = this.interaction.logicDetail || {};
        if (tempCurrentParentLogical && tempCurrentParentLogical.hasOwnProperty(logicAction) && tempCurrentParentLogical[logicAction].logicString) {
          parentQueExpressions = tempCurrentParentLogical[logicAction].logicString.split(/\&\&|\|\|/);
        }
      } else {
        const logicDetail = this.componentConditions?.logicDetail?.[logicAction];
        if (logicDetail?.logicString) {
          parentQueExpressions = logicDetail.logicString.split(/\&\&|\|\|/);
        } else {
          parentQueExpressions = [];
        }
      }
      if (parentQueExpressions.length && this.interaction.sub_questions) {
        var actionProperty = this.actions[logicAction].actionProperty;
        _.each(this.interaction.sub_questions, function (question) {
          if (question.hasOwnProperty(actionProperty) && question[actionProperty]) {
            var checkChildBracket = question[actionProperty].indexOf('((');
            if (checkChildBracket !== 1) {
              question[actionProperty] = question[actionProperty].replace(/\(\(/g, "");
              question[actionProperty] = question[actionProperty].replace(/\)\)/g, "");
            }
            // var childQueExpression = question[actionProperty].split(/\&\&|\|\|/);
            var lastBracketIndex = question[actionProperty].lastIndexOf('(');
            if (lastBracketIndex !== -1) {
              // question[actionProperty] = question[actionProperty].replace(/ *\([^)]*\) */g, "");
              question[actionProperty] = question[actionProperty].substring(0, lastBracketIndex)
              if (question[actionProperty] && question[actionProperty].length > 2) {
                var _operator = question[actionProperty].slice(-2);
                /** Always parent condition append with child by && */
                if (_operator === '&&') {  // || _operator === '||'
                  question[actionProperty] = question[actionProperty].substring(0, question[actionProperty].length - 2);
                }
              }
            }
          }
        });
      }
    } else if (logicAction == 3) {
      var tempCurrentParentLogical = this.interaction.logicDetail || {};
      var parentServerLogicString = '';
      if (tempCurrentParentLogical && tempCurrentParentLogical.hasOwnProperty(logicAction) && tempCurrentParentLogical[logicAction].logicString) {
        parentServerLogicString = tempCurrentParentLogical[logicAction].logicString.split(/\&\&|\|\|/);
      }
      if (this.interaction.sub_questions && parentServerLogicString) {
        var actionProperty = "server-visible-if";
        _.each(this.interaction.sub_questions, function (question) {
          if (question.hasOwnProperty(actionProperty) && question[actionProperty]) {
            // var checkChildBracket = question[actionProperty].indexOf('((');
            // if(checkChildBracket !== 1){
            //     question[actionProperty] = question[actionProperty].replace(/\(\(/g, "");
            //     question[actionProperty] = question[actionProperty].replace(/\)\)/g, "");
            // }
            // var childQueExpression = question[actionProperty].split(/\&\&|\|\|/);
            var lastBracketIndex = question[actionProperty].lastIndexOf('(');
            if (lastBracketIndex !== -1) {
              // question[actionProperty] = question[actionProperty].replace(/ *\([^)]*\) */g, "");
              question[actionProperty] = question[actionProperty].substring(0, lastBracketIndex)
              if (question[actionProperty] && question[actionProperty].length > 2) {
                var _operator = question[actionProperty].slice(-2);
                /** Always parent condition append with child by && */
                if (_operator === '&&') {  // || _operator === '||'
                  question[actionProperty] = question[actionProperty].substring(0, question[actionProperty].length - 2);
                }
              }
            }
          }
        });
      }
    }

    this.addConditionString(logicAction, logicString, logicObjectString);
  }

  isTransformStringObjectValid(expression) {
    if (expression.interaction && expression.comparisonOptr && (expression.target == 'target_field' ? expression.targetInteraction : expression.value)) {
      return true;
    }
    return false;
  };

  /**
  * @name  getQuestionByFieldId
  * @description  getting question object from parent in this case conversation designer
  * @param {string} fieldId - field id 
  * @return {Object} - Question object from conversation designer 
  **/
  getQuestionByFieldId(fieldId) {
    this.questionFields = null;
    if (_.get(this._conversation, 'configuration.fields')) {
      switch (fieldId) {
        case 'primary_rating':
          this.questionFields = _.find(this._conversation.configuration.fields, {
            is_primary_rating: true,
            field_id: fieldId
          });
          break;
        case 'comments':
          this.questionFields = _.find(this._conversation.configuration.fields, {
            is_primary_comment: true,
            field_id: fieldId
          });
          break;
        default:
          this.questionFields = _.find(this._conversation.configuration.fields, {
            field_id: fieldId
          });
      }
    }
    return this.questionFields;
  };

  /**
   * @name  getInteractionFieldString
   * @description get expression question from string
   * @param {Object} question - question object having properties
   * @return {string} logicObjectString - logic object string with ID's
   **/
  getInteractionFieldString(question) {

    if (question.is_primary_rating) {
      return 'rating.primary_rating';
    } else if (question.is_primary_comment) {
      return "rating.comments";
    } else {
      return "rating.detailed_rating['" + question.field_id + "']";
    }
  };

  /**
   * @name  addConditionString
   * @description update or push logic action in connversation designers question
   * @param {string} logicAction - name of logic action
   * @param {string} logicString - logic string for logic action
   * @param {string} logicObjectString - logic object string with ID's
   **/
  addConditionString(logicAction, logicString, logicObjectString) {

    if (typeof logicString == 'string') {
      if (_.get(this, 'interaction.logicDetail')) {
        if (_.get(this.actions, logicAction + '.actionProperty') && logicString != '') {
          this.interaction[this.actions[logicAction].actionProperty] = logicObjectString;
        } else {
          delete this.interaction[this.actions[logicAction].actionProperty];
        }
        if (logicString != '') {
          this.interaction.logicDetail[logicAction] = {
            'logicKey': logicAction,
            'logicString': logicString
          };
        } else {
          delete this.interaction.logicDetail[logicAction];
        }

      } else {
        if (_.get(this.actions, logicAction + '.actionProperty')) {
          this.interaction[this.actions[logicAction].actionProperty] = logicObjectString;
        }
        this.interaction['logicDetail'] = {};
        this.interaction.logicDetail[logicAction] = {
          'logicKey': logicAction,
          'logicString': logicString
        };
      };
    }
    this.markDirty();
  }
  openEditContainer(logicObj, index, valueConfig) {
    setTimeout(function () {
      if (logicObj && !logicObj.isOpen) {
        logicObj.isOpen = true;
        logicObj.isEditMode = true;
      } else if (logicObj && logicObj.isOpen) {
        logicObj.isOpen = false;
        logicObj.isEditMode = false;
      }
    }, 100);
  }

  closeCondition(index) {
    var transformArray = this.componentConditions.logicDetail[this.componentConditions.logicAction].transformString;
    if (transformArray.length === 1) {
      transformArray[0] = { "interaction": '', "comparisonOptr": '', "value": '', "targetInteraction": '', "logicalOptr": "||", "target": "", "configuration": [], "isOpen": true };
    } else {
      if ((transformArray.length - 1) !== index) {
        transformArray.splice(index, 1);
      } else {
        // || transformArray.length === (index + 1)
        transformArray[index] = { "interaction": '', "comparisonOptr": '', "value": '', "targetInteraction": '', "logicalOptr": "||", "target": "", "configuration": [], "isOpen": true };
      }
    }
    var logicAction = this.componentConditions.logicAction;
    if (_.get(this.interaction, this.actions[logicAction].actionProperty)) {
      var logicToDelete = this.interaction[this.actions[logicAction].actionProperty];
      if (_.get(this.interaction, 'sub_questions')) {
        var parentLogicString = this.interaction.logicDetail[logicAction.toString()];
        var subQuestions = this.interaction.sub_questions;
        logicToDelete = '(' + logicToDelete + ')';
        _.each(subQuestions, (subQuestion, index) => {
          if (subQuestion[this.actions[logicAction].actionProperty]) {
            var logicStringOfSubQues = subQuestion[this.actions[logicAction].actionProperty];
            if (logicStringOfSubQues.indexOf('&&' + logicToDelete) !== -1) {
              this.interaction.sub_questions[index][this.actions[logicAction].actionProperty] = logicStringOfSubQues.replace('&&' + logicToDelete, '');
            }
            if (logicStringOfSubQues === logicToDelete) {
              delete this.interaction.sub_questions[index][this.actions[logicAction].actionProperty];
            }

            if (subQuestion.logicDetail && subQuestion.logicDetail.hasOwnProperty(logicAction)) {
              var logicStringOfSubQuesString = subQuestion.logicDetail[logicAction].logicString;
              if (logicStringOfSubQuesString.indexOf('AND' + parentLogicString.logicString) !== -1) {
                this.interaction.sub_questions[index].logicDetail[logicAction].logicString = logicStringOfSubQuesString.replace('AND' + parentLogicString.logicString, '');
              }
            }
          }
        });
      }
    }
    this.updateCurrentCondition(this.componentConditions.logicAction);
  };


  checkForPrimaryRating(event, type) {

    var confirmationMessage = (type == 'is_primary_rating') ? "Set as Primary Question" : "Set as Primary Comment";
    var msg = (type == 'is_primary_rating') ? "Primary Question" : "Primary Comment";
    // type - "is_primary_rating", "is_primary_comment"
    // Only one primary question/comment is allowd
    if (this.interaction[type.toString()]) {
      var primaryInteractions = _.filter(this.conversationFields, (question_item) => {
        return question_item.field_id != this.interaction.field_id && question_item[type.toString()];
      });
      if (primaryInteractions.length >= 1) {
       
        Helpers.smallAlertBox("Error", msg + " is already set", "error", 2000);
        setTimeout(() => {
          this.interaction[type.toString()] = false;
          this.cdr.detectChanges();
        }, 100);
      } else {
        Helpers.showConfirmationBox(confirmationMessage, msg + " cannot be changed. Continue?", (answer) => {
          setTimeout(() => {
            if (!answer) {
              if (type == 'is_primary_rating') {
                if (this.interaction.is_primary_rating) {
                  this.interaction.is_primary_rating = false;
                }
              }

              if (type == 'is_primary_comment') {
                if (this.interaction.is_primary_comment) {
                  this.interaction.is_primary_comment = false;
                }
              }
            } else {
              if (type == 'is_primary_rating') {
                this.interaction.is_mandatory = true;
                this.interaction.is_primary_rating = true;
                this.interaction.is_displayed_on_summary = true;
              }

              if (type == 'is_primary_comment') {
                this.interaction.is_primary_comment = true;
              }
              this.interaction.is_displayed_on_summary = true;
            }
            this.markDirty();
          }, 100)

        });
      }

    } else {
      if (this.interaction.field_id == 'primary_rating' || this.interaction.field_id == 'comments') {
        // once set disallowing to unset
        Helpers.smallAlertBox("Error", msg + " cannot be unset", "error", 2000);

        setTimeout(() => {
          this.interaction[type.toString()] = true;
          this.markDirty();
        }, 100)
      }
    }


  }

  checkForTrendsTab(event, type) {
    if (type == 'is_displayed_on_summary') {
      // Check if this is a primary question (rating or comment)
      const isPrimaryQuestion = this.interaction.is_primary_rating;
      
      // If trying to disable (set to false) and it's a primary question, prevent it
      if (!event && isPrimaryQuestion) {
        const msg = "Primary Question";
        Helpers.smallAlertBox("Warning", msg + " must be displayed on Trends Tab", "warn", 3000);
        
        // Revert the change after a short delay
        setTimeout(() => {
          this.interaction.is_displayed_on_summary = true;
          this.markDirty();
        }, 100);
        return;
      }
      
      this.interaction.is_displayed_on_summary = event;
    }
    this.markDirty();
  }

  setReactorType(value) {
    if (value && this.interaction && !this.interaction.reactor_type) {
      this.interaction.reactor_type = 'ltms-rating-text-reactor';
    }
  }

  markDirty() {
    this.isDirty = false;

    if ((this.interaction.rich_text_enabled && this.interaction.rich_text && this.interaction.rich_text != '') || (!this.interaction.rich_text_enabled && this.interaction.text && this.interaction.text != '')) {
      if (this.componentPropertyEditor && this.generateHash(this.componentPropertyEditor.getValue()) != this.generateHash(this.defaultSchemaEditorValues)) {
        this.isDirty = true
      }
      if (this.generateHash(this.defaultInteraction) != this.generateHash(this.interaction)) {
        this.isDirty = true;
      }
    }
  }

  /**
   * Handle drag and drop for sub_questions to reorder them
   * @param event CdkDragDrop event containing the drag and drop information
   */
  onSubQuestionDrop(event: CdkDragDrop<any[]>) {
    if (event.previousIndex !== event.currentIndex) {
      // Move the item in the sub_questions array
      moveItemInArray(this.interaction.sub_questions, event.previousIndex, event.currentIndex);

      // // Also update the conversationFields array to keep them in sync
      // const movedField = this.conversationFields.find(field => 
      //   field.field_id === this.interaction.sub_questions[event.currentIndex].field_id
      // );
      // if (movedField) {
      //   const fieldIndex = this.conversationFields.findIndex(field => 
      //     field.field_id === movedField.field_id
      //   );
      //   if (fieldIndex !== -1) {
      //     moveItemInArray(this.conversationFields, fieldIndex, fieldIndex + (event.currentIndex - event.previousIndex));
      //   }
      // }

      // Mark the form as dirty since we made changes
      this.markDirty();
    }
  }
}
